'use client';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import React from 'react';
import {
    Box,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Tr,
    useColorModeValue,
    useDisclosure,
    Checkbox,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerHeader,
    DrawerBody,
    useOutsideClick,
    Spinner,
    InputGroup,
    Thead,
    Th,
    Grid,
    GridItem,
    Input,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Image,
    IconButton,
    Tooltip,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    VStack,
    useColorMode,
} from '@chakra-ui/react';
import Select from 'react-select';
import { motion } from 'framer-motion';
import SalesDetails from 'components/salesDetails/SalesDetails';
import { SearchIcon, ViewIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import Columnshowhide from '../components/Columnshowhide';
import SalesPurchaseFilter from '../components/SalesPurchaseFilter';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx-js-style';

// Components
import Card from 'components/card/Card';
import PeriodDatePicker from 'components/RangePicker/PeriodDatePicker';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { getCompanies } from '../../../../redux/actions/company';
import { deleteSaleOrder, getSaleOrderById } from '../../../../redux/actions/salesOrder';
import { deleteReportFormat, getReportFormat, getReportSaleOrderDetail } from '../../../../redux/actions/reports';
import DeletePasswordModal from 'views/admin/sales/Pages/SalesInvoiceMaster/DeletePasswordModal';

// Utils
import { getShareCompanyOptions } from 'utils/util';
import { deleteRecord } from 'utils/util';
import { REPORT_TYPE } from 'constants';

// Assets
import nodata from 'assets/img/nfts/no data.svg';
import Download from '../../../../assets/img/nfts/download.svg';
import xls from '../../../../../src/assets/img/nfts/xls.svg';
import back from '../../../../../src/assets/img/nfts/back.svg';
import DotIcon from '../../../../assets/img/nfts/dot.svg';
import { MdCompress, MdDelete, MdExpand, MdOutlineRefresh } from 'react-icons/md';
import { useGlobal } from 'contexts';
import { IoFilterOutline } from 'react-icons/io5';
import ColumnShowHide from '../components/Columnshowhide';
import { LucideEdit } from 'lucide-react';
import { FiPlusCircle } from 'react-icons/fi';
import { useUser } from 'contexts';
import { deleteRecordForSales } from 'utils/util';
import Loader from 'views/admin/default/Loader';
import { VariableSizeList } from 'react-window';

// Constants
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_DELAY = 150;
const REPORT_TYPE_OPTIONS = [
    { value: 'date', label: 'Date' },
    { value: 'party', label: 'Party' },
    { value: 'broker', label: 'Broker' },
];
const FORMAT_TYPE_OPTIONS = [
    { value: 'with_item_detail', label: 'With Item Detail' },
    { value: 'without_item_detail', label: 'Without Item Detail' },
];

const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

// Helper Components
const ColumnVisibilityModal = React.memo(({ isOpen, onClose, columns, columnVisibility, setColumnVisibility }) => (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
            <ModalHeader>Show/Hide Columns</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
                {columns.map((column) => (
                    <Flex key={column.id} mb={2} alignItems="center">
                        <Checkbox
                            isChecked={!!columnVisibility[column.id]}
                            onChange={(e) => {
                                setColumnVisibility((prev) => ({
                                    ...prev,
                                    [column.id]: e.target.checked,
                                }));
                            }}
                            mr={2}
                        />
                        <Text>{column.header}</Text>
                    </Flex>
                ))}
            </ModalBody>
            <ModalFooter>
                <Button colorScheme="blue" onClick={onClose}>
                    Close
                </Button>
            </ModalFooter>
        </ModalContent>
    </Modal>
));

const SalesOrderDetail = () => {
    // Refs
    const fromDateInputRef = useRef(null);
    const wrapperRef = useRef();
    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    const dataCacheRef = useRef({});
    const { userGlobalSettingsData } = useGlobal();

    // State
    const [companyFilter, setCompanyFilter] = useState([]);
    const [reportType, setReportType] = useState('date');
    const [formatType, setFormatType] = useState('with_item_detail');
    const [expandedItems, setExpandedItems] = useState({});
    const [sorting, setSorting] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState({});
    const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
    const [isShowHideColumn, setIsShowHideColumn] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [isEditShowHide, setIsEditShowHide] = useState(false);
    const [editData, setEditData] = useState({});
    const [filteredResults, setFilteredResults] = useState(null);
    const [allExpanded, setAllExpanded] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [dataId, setDataId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';
    const listRef = useRef(null);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const isFirstRenderFilter = useRef(true);
    const expandedItemsRef = useRef(expandedItems);
    const [containerHeight, setContainerHeight] = useState(700);
    const [flattenedData, setFlattenedData] = useState([]);
    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(today.getDate() - 6);

        return {
            fromDate: sevenDaysAgo.toISOString().split('T')[0], // last 7th day
            toDate: today.toISOString().split('T')[0], // today
        };
    });

    // Redux
    const dispatch = useDispatch();
    const salesReport = useSelector((state) => state.reports?.saleOrderDetail || []);
    const daybookData = useSelector((state) => state.daybook?.list || []);
    const reportformat = useSelector((state) => state.reports?.reportFormat || []);
    const viewData = useSelector((state) => state.salesOrder?.single);
    const { viewDetail, paginate } = useMemo(() => {
        const data = viewData;
        const pagination = viewData?.pagination;

        return { viewDetail: data, paginate: pagination };
    }, [viewData]);
    // console.log('viewData', viewData?.data[0]?.data);

    const [isEditFormOpen, setIsEditFormOpen] = React.useState(false);
    const [isOpenPurchaseReturnSingleView, setIsOpenPurchaseReturnSingleView] = React.useState(null);

    const { isOpen, onOpen, onClose } = useDisclosure();

    // Theme and styles
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const backgroundColor = useColorModeValue('white', '#101F4A');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const bg = useColorModeValue('#f2f7f2', '#1a254b');
    const icon = useColorModeValue('#000000', '#ffffff');
    const controlBg = useColorModeValue('#FFFFFF', '#2D3748');
    const controlBorder = useColorModeValue('#E2E8F0', '#4A5568');
    const controlBorderHover = useColorModeValue('#CBD5E0', '#718096');
    const focusBoxShadow = useColorModeValue('0 0 0 1px #3182CE', '0 0 0 1px #63B3ED');
    const placeholderColor = useColorModeValue('#A0AEC0', '#718096');
    const optionBg = useColorModeValue('#FFFFFF', '#2D3748');
    const optionHoverBg = useColorModeValue('#EDF2F7', '#4A5568');
    const [stockStatus, setStockStatus] = useState(0);
    const menuBg = useColorModeValue('#FFFFFF', '#2D3748');
    const menuBorder = useColorModeValue('#E2E8F0', '#4A5568');
    const indicatorColor = useColorModeValue('#718096', '#A0AEC0');
    const indicatorHoverColor = useColorModeValue('#4A5568', '#CBD5E0');
    const isStaticReportType = ['date', 'party', 'broker'].includes(reportType);
    const { companyList, company } = useGlobal();
    const { user } = useUser();
    const dbName = company?.dbName;
    const companyId = Number(localStorage.getItem('companyId'));
    const userRights = user?.user?.userRights || user?.userRights;
    const saleOrderRight = Array.isArray(userRights)
        ? userRights.find((right) => right.companyId === companyId)?.saleorder
        : null;
    // Memoized values
    const companyOptions = useMemo(() => {
        return getShareCompanyOptions({ dbName, company: companyList });
    }, [companyList, dbName]);

    const lastcompanyOptions = useMemo(() => {
        const currentUserCode = user?.loginhistory?.code;
        const currentCompanyId = user?.loginhistory?.companyId;

        // SHARED COMPANY MODE: When code is present
        if (currentUserCode) {
            // Find the company with matching ID AND matching dbName
            const sharedCompany = companyList.find(
                company => company.id === currentCompanyId && company.dbName === currentUserCode
            );

            if (sharedCompany) {
                return [{
                    value: sharedCompany.id,
                    label: sharedCompany.name,
                    id: sharedCompany.id,
                    name: sharedCompany.name,
                }];
            }

            // Fallback: try to find by dbName only
            const companiesWithDbName = companyList.filter(company => company.dbName === currentUserCode);
            if (companiesWithDbName.length > 0) {
                return companiesWithDbName.map(company => ({
                    value: company.id,
                    label: company.name,
                    id: company.id,
                    name: company.name,
                }));
            }

            return [];
        }

        // NORMAL MODE: When no code, show companies without dbName
        return companyList
            .filter(company => !company.dbName)
            .map(company => ({
                value: company.id,
                label: company.name,
                id: company.id,
                name: company.name,
            }));
    }, [companyList, user?.loginhistory?.code, user?.loginhistory?.companyId]);
    const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
    const companyIds = useMemo(
        () =>
            companyFilter.length > 0
                ? companyFilter
                : localStorage.getItem('companyId')
                    ? [parseInt(localStorage.getItem('companyId'))]
                    : [],
        [companyFilter],
    );
    const [showHideData, setShowHideData] = useState({});

    const allowedDefNames = ['SalesOrderDetailDate', 'SalesOrderDetailParty', 'SalesOrderDetailBroker'];
    const dynamicOptions = reportformat
        .filter((item) => item.defName && allowedDefNames.includes(item.defName))
        .map((item) => ({
            id: item.id,
            value: item.defName && item.title,
            label: item.title,
            isDynamic: true,
            formatData: item,
        }));

    const dynamicValues = dynamicOptions.map((option) => option.value);
    const dynamicOptionValues = dynamicOptions.find((option) => option.value === reportType);
    const dynamicEditOption = dynamicOptionValues?.formatData.defName;
    const handleReportTypeChange = (selectedOption) => {
        setReportType(selectedOption.value);
        setExpandedItems({});
        dispatch(getReportFormat());
    };

    const getColumnsReportsSalesDetail = useCallback((formatType, reportType) => {
        const isPartyReport = reportType === 'party' || dynamicEditOption === 'SalesOrderDetailParty';
        const isDateReport = dynamicEditOption === 'SalesOrderDetailDate';

        const viewJson = dynamicOptionValues?.formatData?.viewJson || [];

        const convertBackendColor = (backendColor) => {
            if (!backendColor) return null;
            if (backendColor.startsWith('#')) return backendColor;
            if (backendColor.startsWith('0xff')) return '#' + backendColor.substring(4);
            if (/^[0-9A-Fa-f]{6}$/.test(backendColor)) return '#' + backendColor;
            return backendColor;
        };

        const getColumnInfo = (defName) => {
            const viewItem = viewJson.find(
                (item) =>
                    item?.defName?.toLowerCase() === defName.toLowerCase() ||
                    item?.title?.toLowerCase() === defName.toLowerCase(),
            );

            if (!viewItem || isStaticReportType) {
                return {
                    title: defName,
                    isShow: true,
                    color: textColor,
                    isBold: false,
                    isItalic: false,
                    orderNo: 0,
                    flex: 1,
                };
            }

            return {
                title: viewItem.title || defName,
                isShow: viewItem.isShow === 'true' || viewItem.isShow === true,
                color: viewItem.color ? convertBackendColor(viewItem.color) : textColor,
                isBold: viewItem.isBold === 'true' || viewItem.isBold === true,
                isItalic: viewItem.isItalic === 'true' || viewItem.isItalic === true,
                orderNo: viewItem.orderNo ? parseInt(viewItem.orderNo) : 0,
                flex: viewItem.flex ? parseInt(viewItem.flex) : 1,
            };
        };

        const baseColumns = [
            {
                id: 'transactionAt',
                header: getColumnInfo('date').title,
                accessor: 'transactionAt',
                isShow: getColumnInfo('date').isShow,
                color: getColumnInfo('date').color,
                isBold: getColumnInfo('date').isBold,
                isItalic: getColumnInfo('date').isItalic,
                orderNo: getColumnInfo('date').orderNo,
                flex: getColumnInfo('date').flex,
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format('DD-MM-YYYY') : value;
                },
            },
            {
                id: 'docno',
                header: getColumnInfo('doc').title,
                accessor: 'docno',
                isShow: getColumnInfo('doc').isShow,
                color: getColumnInfo('doc').color,
                isBold: getColumnInfo('doc').isBold,
                isItalic: getColumnInfo('doc').isItalic,
                orderNo: getColumnInfo('doc').orderNo,
                flex: getColumnInfo('doc').flex,
            },
            ...(!isPartyReport
                ? [
                    {
                        id: 'accountname',
                        header: getColumnInfo('party').title,
                        accessor: 'accountname',
                        isShow: getColumnInfo('party').isShow,
                        color: getColumnInfo('party').color,
                        isBold: getColumnInfo('party').isBold,
                        isItalic: getColumnInfo('party').isItalic,
                        orderNo: getColumnInfo('party').orderNo,
                        flex: getColumnInfo('party').flex,
                    },
                ]
                : []),
            ...(reportType === 'date'
                ? [
                    {
                        id: 'mobile',
                        header: getColumnInfo('mobile').title,
                        accessor: 'mobile',
                        isShow: getColumnInfo('mobile').isShow,
                        color: getColumnInfo('mobile').color,
                        isBold: getColumnInfo('mobile').isBold,
                        isItalic: getColumnInfo('mobile').isItalic,
                        orderNo: getColumnInfo('mobile').orderNo,
                        flex: getColumnInfo('mobile').flex,
                    },
                ]
                : []),
            {
                id: 'daybookname',
                header: getColumnInfo('daybook').title,
                accessor: 'daybookname',
                isShow: getColumnInfo('daybook').isShow,
                color: getColumnInfo('daybook').color,
                isBold: getColumnInfo('daybook').isBold,
                isItalic: getColumnInfo('daybook').isItalic,
                orderNo: getColumnInfo('daybook').orderNo,
                flex: getColumnInfo('daybook').flex,
            },
            {
                id: 'salesmanname',
                header: getColumnInfo('salesman').title,
                accessor: 'salesmanname',
                isShow: getColumnInfo('salesman').isShow,
                color: getColumnInfo('salesman').color,
                isBold: getColumnInfo('salesman').isBold,
                isItalic: getColumnInfo('salesman').isItalic,
                orderNo: getColumnInfo('salesman').orderNo,
                flex: getColumnInfo('salesman').flex,
            },
            {
                id: 'notes',
                header: getColumnInfo('notes').title,
                accessor: 'notes',
                isShow: getColumnInfo('notes').isShow,
                color: getColumnInfo('notes').color,
                isBold: getColumnInfo('notes').isBold,
                isItalic: getColumnInfo('notes').isItalic,
                orderNo: getColumnInfo('notes').orderNo,
                flex: getColumnInfo('notes').flex,
            },
            {
                id: 'qty1',
                header: getColumnInfo('ORDqty1').title,
                accessor: 'qty1',
                isNumeric: true,
                isShow: getColumnInfo('qty1').isShow,
                color: getColumnInfo('qty1').color,
                isBold: getColumnInfo('qty1').isBold,
                isItalic: getColumnInfo('qty1').isItalic,
                orderNo: getColumnInfo('qty1').orderNo,
                flex: getColumnInfo('qty1').flex,
            },
            {
                id: 'qty2',
                header: getColumnInfo('ORDqty2').title,
                accessor: 'qty2',
                isNumeric: true,
                isShow: getColumnInfo('qty2').isShow,
                color: getColumnInfo('qty2').color,
                isBold: getColumnInfo('qty2').isBold,
                isItalic: getColumnInfo('qty2').isItalic,
                orderNo: getColumnInfo('qty2').orderNo,
                flex: getColumnInfo('qty2').flex,
            },
            {
                id: 'qty3',
                header: getColumnInfo('ORDqty3').title,
                accessor: 'qty3',
                isNumeric: true,
                isShow: getColumnInfo('qty3').isShow,
                color: getColumnInfo('qty3').color,
                isBold: getColumnInfo('qty3').isBold,
                isItalic: getColumnInfo('qty3').isItalic,
                orderNo: getColumnInfo('qty3').orderNo,
                flex: getColumnInfo('qty3').flex,
            },
            {
                id: 'deliveredQty1',
                header: getColumnInfo('DelQty1').title || 'Del Qty1',
                // ↓ accessor function — row se processedList sum karo
                accessor: (row) =>
                    (row.salesOrderLineItems || []).reduce((sum, li) =>
                        sum + (li.processedList || []).reduce((a, p) => a + Number(p.qty1 || 0), 0), 0),
                isNumeric: true,
                isShow: getColumnInfo('deliveredQty1').isShow,
                color: getColumnInfo('deliveredQty1').color,
                isBold: getColumnInfo('deliveredQty1').isBold,
                isItalic: getColumnInfo('deliveredQty1').isItalic,
                orderNo: getColumnInfo('deliveredQty1').orderNo,
                flex: getColumnInfo('deliveredQty1').flex,
            },
            {
                id: 'deliveredQty2',
                header: getColumnInfo('DelQty2').title || 'Del Qty2',
                accessor: (row) =>
                    (row.salesOrderLineItems || []).reduce((sum, li) =>
                        sum + (li.processedList || []).reduce((a, p) => a + Number(p.qty2 || 0), 0), 0),
                isNumeric: true,
                isShow: getColumnInfo('deliveredQty2').isShow,
                color: getColumnInfo('deliveredQty2').color,
                isBold: getColumnInfo('deliveredQty2').isBold,
                isItalic: getColumnInfo('deliveredQty2').isItalic,
                orderNo: getColumnInfo('deliveredQty2').orderNo,
                flex: getColumnInfo('deliveredQty2').flex,
            },
            {
                id: 'deliveredQty3',
                header: getColumnInfo('DelQty3').title || 'Del Qty3',
                accessor: (row) =>
                    (row.salesOrderLineItems || []).reduce((sum, li) =>
                        sum + (li.processedList || []).reduce((a, p) => a + Number(p.qty3 || 0), 0), 0),
                isNumeric: true,
                isShow: getColumnInfo('deliveredQty3').isShow,
                color: getColumnInfo('deliveredQty3').color,
                isBold: getColumnInfo('deliveredQty3').isBold,
                isItalic: getColumnInfo('deliveredQty3').isItalic,
                orderNo: getColumnInfo('deliveredQty3').orderNo,
                flex: getColumnInfo('deliveredQty3').flex,
            },
            {
                id: 'pendingQty1',
                header: getColumnInfo('PNDQty1').title,
                accessor: 'pendingQty1',
                isNumeric: true,
                isShow: getColumnInfo('penQty1').isShow,
                color: getColumnInfo('penQty1').color,
                isBold: getColumnInfo('penQty1').isBold,
                isItalic: getColumnInfo('penQty1').isItalic,
                orderNo: getColumnInfo('pendQty1').orderNo,
                flex: getColumnInfo('penQty1').flex,
            },
            {
                id: 'pendingQty2',
                header: getColumnInfo('PNDQty2').title,
                accessor: 'pendingQty2',
                isNumeric: true,
                isShow: getColumnInfo('pendingQty2').isShow,
                color: getColumnInfo('pendingQty2').color,
                isBold: getColumnInfo('pendingQty2').isBold,
                isItalic: getColumnInfo('pendingQty2').isItalic,
                orderNo: getColumnInfo('pendingQty2').orderNo,
                flex: getColumnInfo('pendingQty2').flex,
            },
            {
                id: 'pendingQty3',
                header: getColumnInfo('PNDQty3').title,
                accessor: 'pendingQty3',
                isNumeric: true,
                isShow: getColumnInfo('pendingQty3').isShow,
                color: getColumnInfo('pendingQty3').color,
                isBold: getColumnInfo('pendingQty3').isBold,
                isItalic: getColumnInfo('pendingQty3').isItalic,
                orderNo: getColumnInfo('pendingQty3').orderNo,
                flex: getColumnInfo('pendingQty3').flex,
            },
            {
                id: 'netAmount', // KEEP THIS ONE
                header: getColumnInfo('netAmt').title,
                accessor: 'netAmount',
                isNumeric: true,
                isShow: getColumnInfo('netAmt').isShow,
                color: getColumnInfo('netAmt').color,
                isBold: getColumnInfo('netAmt').isBold,
                isItalic: getColumnInfo('netAmt').isItalic,
                orderNo: getColumnInfo('netAmt').orderNo,
                flex: getColumnInfo('netAmt').flex,
                cell: (info) => {
                    const value = info.getValue();
                    return `₹${Number(value || 0).toLocaleString()}`;
                },
            },
            {
                id: 'billAmount', // CHANGED FROM netAmount TO billAmount
                header: getColumnInfo('billAmt').title,
                accessor: 'netAmount', // Still using netAmount as accessor since data might have same value
                isNumeric: true,
                isShow: getColumnInfo('billAmt').isShow,
                color: getColumnInfo('billAmt').color,
                isBold: getColumnInfo('billAmt').isBold,
                isItalic: getColumnInfo('billAmt').isItalic,
                orderNo: getColumnInfo('billAmt').orderNo,
                flex: getColumnInfo('billAmt').flex,
                cell: (info) => {
                    const value = info.getValue();
                    return `₹${Number(value || 0).toLocaleString()}`;
                },
            },
        ];

        let itemDetailColumns = [];

        if (formatType === 'with_item_detail') {
            if (reportType === 'date') {
                itemDetailColumns = [
                    {
                        id: 'itemName',
                        header: getColumnInfo('itemName').title,
                        accessor: 'itemName',
                        isShow: getColumnInfo('itemName').isShow,
                        color: getColumnInfo('itemName').color,
                        isBold: getColumnInfo('itemName').isBold,
                        isItalic: getColumnInfo('itemName').isItalic,
                        orderNo: getColumnInfo('itemName').orderNo,
                        flex: getColumnInfo('itemName').flex,
                        cell: (info) => {
                            const value = info.getValue();
                            return (
                                <div
                                    className="text-center px-2 py-1"
                                    style={{
                                        textAlign: 'center',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        maxWidth: '200px',
                                        minWidth: '150px',
                                    }}
                                    title={value}>
                                    {value}
                                </div>
                            );
                        },
                    },
                    {
                        id: 'itemRate',
                        header: getColumnInfo('rate').title,
                        accessor: 'itemRate',
                        isNumeric: true,
                        isShow: getColumnInfo('rate').isShow,
                        color: getColumnInfo('rate').color,
                        isBold: getColumnInfo('rate').isBold,
                        isItalic: getColumnInfo('rate').isItalic,
                        orderNo: getColumnInfo('rate').orderNo,
                        flex: getColumnInfo('rate').flex,
                    },
                ];
            } else {
                itemDetailColumns = [
                    {
                        id: 'itemName',
                        header: getColumnInfo('itemName').title,
                        accessor: 'itemName',
                        isShow: getColumnInfo('itemName').isShow,
                        color: getColumnInfo('itemName').color,
                        isBold: getColumnInfo('itemName').isBold,
                        isItalic: getColumnInfo('itemName').isItalic,
                        orderNo: getColumnInfo('itemName').orderNo,
                        flex: getColumnInfo('itemName').flex,
                        cell: (info) => {
                            const value = info.getValue();
                            return (
                                <div
                                    className="text-center px-2 py-1"
                                    style={{
                                        textAlign: 'center',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        maxWidth: '200px',
                                        minWidth: '150px',
                                    }}
                                    title={value}>
                                    {value}
                                </div>
                            );
                        },
                    },
                    {
                        id: 'itemRate',
                        header: getColumnInfo('rate').title,
                        accessor: 'itemRate',
                        isNumeric: true,
                        isShow: getColumnInfo('rate').isShow,
                        color: getColumnInfo('rate').color,
                        isBold: getColumnInfo('rate').isBold,
                        isItalic: getColumnInfo('rate').isItalic,
                        orderNo: getColumnInfo('rate').orderNo,
                        flex: getColumnInfo('rate').flex,
                    },
                    ...(!isDateReport
                        ? [
                            {
                                id: 'hsnSac',
                                header: getColumnInfo('HSN/SAC').title || 'HSN/SAC',
                                accessor: 'hsnSac',
                                isShow: getColumnInfo('HSN/SAC').isShow,
                                color: getColumnInfo('HSN/SAC').color,
                                isBold: getColumnInfo('HSN/SAC').isBold,
                                isItalic: getColumnInfo('HSN/SAC').isItalic,
                                orderNo: getColumnInfo('HSN/SAC').orderNo,
                                flex: getColumnInfo('HSN/SAC').flex,
                            },
                            {
                                id: 'dueDays',
                                header: getColumnInfo('Due Days').title || 'Due Days',
                                accessor: 'dueDays',
                                isShow: getColumnInfo('Due Days').isShow,
                                color: getColumnInfo('Due Days').color,
                                isBold: getColumnInfo('Due Days').isBold,
                                isItalic: getColumnInfo('Due Days').isItalic,
                                orderNo: getColumnInfo('Due Days').orderNo,
                                flex: getColumnInfo('Due Days').flex,
                            },
                        ]
                        : []),
                ];
            }

            const insertIndex =
                reportType === 'date'
                    ? baseColumns.findIndex((col) => col.id === 'mobile') + 1
                    : baseColumns.findIndex((col) => col.id === 'docno') + 1;

            baseColumns.splice(insertIndex, 0, ...itemDetailColumns);
        }

        return baseColumns
            .filter((col) => col.isShow !== false)
            .sort((a, b) => {
                const orderA = a.orderNo || 0;
                const orderB = b.orderNo || 0;
                return orderA - orderB;
            });
    });
    const [qtyVisibility, setQtyVisibility] = useState({
        qty1: true,
        qty2: false,
        qty3: false,
    });
    const staticReportTypeOptions = [
        { value: 'date', label: 'Date' },
        { value: 'party', label: 'Party' },
        { value: 'broker', label: 'Broker' },
    ];

    const stockReportTypeOptions = [
        { value: 0, label: 'All' },
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Processed' },
    ];


    const handleStockStatusChange = (selectedOption) => {
        setStockStatus(selectedOption.value);
        setExpandedItems({});
        dispatch(getReportFormat());
    };

    const reportTypeOptions = [
        ...staticReportTypeOptions,
        ...(dynamicOptions.length > 0
            ? [
                {
                    label: 'Custom Formats',
                    options: dynamicOptions,
                },
            ]
            : []),
    ];
    const staticOption = staticReportTypeOptions.find((option) => option.value === reportType);

    const dynamicColumns = useMemo(() => {
        const format = reportformat.find((f) => f.id === reportType) || reportformat[0];
        return getColumnsReportsSalesDetail(formatType, reportType, format);
    }, [formatType, reportType, reportformat]);

    const columnsData = useMemo(() => {
        return dynamicColumns.map((col, index) => ({
            key: col?.id,
            label: col?.header,
            title: col?.header,
            width: col?.width, // default width if not defined
            index: index + 1,
            status: true,
        }));
    }, [dynamicColumns]);

    useEffect(() => {
        dispatch(getReportFormat());
    }, [dispatch]);

    const formatOptionLabel = ({ label, isDynamic, id }, { context }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{label}</span>
            {isDynamic && context === 'menu' && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFormat(id);
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#ff4d4f',
                        padding: '0 5px',
                    }}
                    title="Delete this format">
                    <MdDelete />
                </button>
            )}
        </div>
    );

    const handleDeleteFormat = async (id) => {
        try {
            await dispatch(deleteReportFormat(id));

            if (editData?.id === id) {
                setIsEditShowHide(false);
                setEditData({});
            }

            // Only refetch if we're currently using a dynamic format that was deleted
            if (dynamicValues.includes(reportType)) {
                dispatch(getReportFormat());
            }
        } catch (error) {
            console.error('Delete error:', error);
        }
    };

    useEffect(() => {
        // Only fetch if we don't have report formats yet
        if (reportformat.length === 0) {
            dispatch(getReportFormat());
        }
    }, [dispatch, reportformat.length]);

    useEffect(() => {
        expandedItemsRef.current = expandedItems;
    }, [expandedItems]);

    const handleNextPage = () => {
        console.log('Next');
    };
    const handlePrevPage = () => {
        console.log('Prev');
    };

    const calculateColumnWidths = () => {
        const visibleColumns = dynamicColumns.filter((col) => columnVisibility[col.id]);
        const totalFlex = visibleColumns.reduce((sum, col) => sum + (col.flex || 1), 0);
        const availableWidth = 100; // This represents 100% of the table width

        return visibleColumns.map((col) => {
            const flexValue = col.flex || 1;
            return `${(flexValue / totalFlex) * availableWidth}%`;
        });
    };

    const columnWidths = calculateColumnWidths();
    useEffect(() => {
        if (Object.keys(showHideData).length === 0) {
            setShowHideData(columnsData);
        }
    }, [showHideData, columnsData]);

    const flattenData = useCallback(
        (data) => {
            if (!data) return [];

            if (reportType === REPORT_TYPE.party) {
                return data.flatMap((company) => company.data?.flatMap((account) => account.data || []) || []);
            }
            return data.flatMap((dateItem) => dateItem.data?.flatMap((item) => item.data || []) || []);
        },
        [reportType],
    );

    // Table setup
    const columnHelper = createColumnHelper();
    const columns = useMemo(
        () =>
            dynamicColumns.map((column) =>
                columnHelper.accessor(column.id, {
                    id: column.id,
                    header: () => (
                        <Text
                            justifyContent="space-between"
                            align="center"
                            fontSize={{ sm: '10px', lg: '12px' }}
                            color={textColor}>
                            {column.header}
                        </Text>
                    ),
                    cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
                }),
            ),
        [columnHelper, dynamicColumns, textColor],
    );

    const table = useReactTable({
        data: filteredResults || flattenedData,
        columns,
        state: { sorting, columnVisibility },
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    // Effects

    useEffect(() => {
        const initialColumnVisibility = {};
        dynamicColumns.forEach((column) => {
            initialColumnVisibility[column.id] = true;
        });
        setColumnVisibility(initialColumnVisibility);
    }, [dynamicColumns]);

    const storedCompanyId = localStorage.getItem('companyId');
    useEffect(() => {
        if (storedCompanyId && companyList.length > 0) {
            const companyId = parseInt(storedCompanyId);
            const matchingCompany = companyList.find((comp) => comp.id === companyId);
            if (matchingCompany && !companyFilter.includes(matchingCompany.id)) {
                setCompanyFilter([matchingCompany.id]);
            }
        }
    }, [companyList, storedCompanyId]);

    useEffect(() => {
        setFlattenedData(flattenData(salesReport));
    }, [salesReport, reportType, flattenData]);

    const fetchData = useCallback(
        async (searchQuery = '') => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            const storedId = localStorage.getItem('companyId');
            const parsedStoredId = storedId ? parseInt(storedId, 10) : null;

            const companyIds =
                companyFilter.length > 0
                    ? companyFilter
                    : parsedStoredId && !isNaN(parsedStoredId)
                        ? [parsedStoredId]
                        : [];

            if (!companyIds.length || !dateRange.fromDate || !dateRange.toDate) return;

            setIsLoading(true);

            try {
                const cacheKey = `${companyIds.toString()}-${reportType}-${dateRange.fromDate}-${dateRange.toDate}-${searchQuery}`;

                if (dataCacheRef.current[cacheKey]) {
                    setFilteredResults(dataCacheRef.current[cacheKey]);
                    setIsLoading(false);
                    return;
                }

                const result = await dispatch(
                    getReportSaleOrderDetail({
                        companyIds: companyIds.toString(),
                        type: reportType,
                        fromDate: dateRange.fromDate,
                        toDate: dateRange.toDate,
                        stockStatus: stockStatus,
                        search: searchQuery.trim(),
                    }),
                    { signal: abortControllerRef.current.signal },
                );

                dataCacheRef.current[cacheKey] = result;

            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching sales details:', error);
                }
            } finally {
                setIsLoading(false);
                abortControllerRef.current = null;
            }
        },
        [dispatch, companyFilter, reportType, dateRange.fromDate, dateRange.toDate, stockStatus],
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData();
        }, 300); // Small debounce

        return () => clearTimeout(timer);
    }, [fetchData]);

    // Event handlers
    const handleSearchChange = useCallback(
        (e) => {
            const value = e.target.value;
            setSearchTerm(value);

            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }

            searchTimeoutRef.current = setTimeout(() => {
                if (value === '' || value.length >= MIN_SEARCH_LENGTH) {
                    // Use cached results if available for this search term
                    if (dataCacheRef.current[value]) {
                        setFilteredResults(dataCacheRef.current[value]);
                        return;
                    }
                    fetchData(value);
                } else if (value === '') {
                    // If search is cleared, show all data
                    fetchData('');
                }
            }, SEARCH_DEBOUNCE_DELAY);
        },
        [fetchData],
    );

    const handleCompanyChange = useCallback((selectedOptions) => {
        setCompanyFilter(selectedOptions ? selectedOptions : []);
    }, []);

    const handleDateRangeChange = useCallback((dates) => {
        if (!dates || dates.length !== 2) return;
        const [from, to] = dates.map((date) => (typeof date === 'string' ? date : date.toISOString().slice(0, 10)));
        setDateRange({ fromDate: from, toDate: to });
    }, []);

    const toggleExpand = useCallback((key) => {
        setExpandedItems(prev => {
            const next = { ...prev, [key]: !prev[key] };
            expandedItemsRef.current = next; // keep ref in sync
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        const newExpanded = {};

        if (reportType === REPORT_TYPE.party) {
            // For party report type
            salesReport.forEach((companyData) => {
                const companyKey = `company-${companyData.companyId}`;
                newExpanded[companyKey] = true;

                companyData.data?.forEach((accountData) => {
                    const accountKey = `account-${accountData.accountId}`;
                    newExpanded[accountKey] = true;
                });
            });
        } else {
            // For other report types (date/broker)
            salesReport.forEach((dateItem) => {
                dateItem.data?.forEach((itemData) => {
                    const itemKey = `item-${itemData.itemId}`;
                    newExpanded[itemKey] = true;
                });
            });
        }

        setExpandedItems(newExpanded);
        setAllExpanded(true);
    }, [salesReport, reportType]);

    const collapseAll = useCallback(() => {
        const newExpanded = {};
        salesReport.forEach((companyData) => {
            const companyKey = `company-${companyData.companyId}`;
            newExpanded[companyKey] = true; // Keep companies expanded

            if (companyData.items) {
                companyData.items.forEach((itemData) => {
                    const itemKey = `item-${itemData.item_id}-${companyData.companyId}`;
                    newExpanded[itemKey] = false; // Collapse all items
                });
            }
        });
        setExpandedItems(newExpanded);
        setAllExpanded(false);
    }, [salesReport]);

    useEffect(() => {
        if (isFirstRenderFilter.current) {
            isFirstRenderFilter.current = false;
            return;
        }
        setIsFilterLoading(false);
    }, [salesReport]);

    const initializeExpandedItems = useCallback(
        (data) => {
            if (!data || !Array.isArray(data)) return {};

            const initialExpandedState = {};

            if (reportType === REPORT_TYPE.party) {
                data.forEach((companyData) => {
                    // Expand company by default
                    initialExpandedState[`company-${companyData.companyId}`] = true;

                    // Expand all accounts by default
                    if (companyData.data && Array.isArray(companyData.data)) {
                        companyData.data.forEach((accountData) => {
                            initialExpandedState[`account-${accountData.accountId}`] = true;
                        });
                    }
                });
            }

            return initialExpandedState;
        },
        [reportType],
    );

    useEffect(() => {
        if (salesReport && salesReport.length > 0) {
            const initialExpandedState = initializeExpandedItems(salesReport);
            setExpandedItems(initialExpandedState);
        }
    }, [salesReport, initializeExpandedItems]);

    useOutsideClick({
        ref: wrapperRef,
        handler: () => {
            if (searchTerm === '') setExpanded(false);
        },
    });

    const downloadExcel = useCallback(() => {
        if (!salesReport || salesReport.length === 0) {
            alert('No data available to export');
            return;
        }

        try {
            const wb = XLSX.utils.book_new();

            const parseDate = (v) => {
                if (!v) return null;
                if (v instanceof Date && !isNaN(v.getTime())) return v;
                if (typeof v === 'string') {
                    if (/^\d{2}-\d{2}-\d{4}$/.test(v)) { const [d, m, y] = v.split('-'); return new Date(+y, +m - 1, +d); }
                    if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) { const [d, m, y] = v.split('/'); return new Date(+y, +m - 1, +d); }
                    if (/^\d{4}-\d{2}-\d{2}/.test(v)) { const dt = new Date(v); if (!isNaN(dt)) return dt; }
                }
                if (!isNaN(v)) { const ts = +v; const d = ts > 9_999_999_999 ? new Date(ts) : new Date(ts * 1000); if (!isNaN(d)) return d; }
                return null;
            };

            const fmtDateStr = (v) => {
                if (!v) return '-';
                const d = parseDate(v);
                return d
                    ? `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
                    : String(v);
            };

            const isDynamic = dynamicValues?.includes(reportType);
            const fmtLabel = isDynamic
                ? (dynamicOptions?.find(o => o.value === reportType)?.label || reportType)
                : (staticReportTypeOptions?.find(o => o.value === reportType)?.label || reportType);

            const COLUMNS = [
                { id: 'transactionAt', header: 'Date', isNumeric: false, w: 13 },
                { id: 'docno', header: 'Doc No', isNumeric: false, w: 9 },
                { id: 'accountname', header: 'Party', isNumeric: false, w: 24 },
                { id: 'mobile', header: 'Mobile', isNumeric: false, w: 13 },
                { id: 'itemName', header: 'Item Name', isNumeric: false, w: 26 },
                { id: 'itemRate', header: 'Rate', isNumeric: true, w: 9 },
                { id: 'daybookname', header: 'Daybook', isNumeric: false, w: 20 },
                { id: 'salesmanname', header: 'Salesman', isNumeric: false, w: 16 },
                { id: 'notes', header: 'Notes', isNumeric: false, w: 18 },
                { id: 'qty1', header: 'Ord Qty1', isNumeric: true, w: 10 },
                { id: 'qty2', header: 'Ord Qty2', isNumeric: true, w: 10 },
                { id: 'qty3', header: 'Ord Qty3', isNumeric: true, w: 10 },
                { id: 'deliveredQty1', header: 'Del Qty1', isNumeric: true, w: 10 },
                { id: 'deliveredQty2', header: 'Del Qty2', isNumeric: true, w: 10 },
                { id: 'deliveredQty3', header: 'Del Qty3', isNumeric: true, w: 10 },
                { id: 'pendingQty1', header: 'Pnd Qty1', isNumeric: true, w: 10 },
                { id: 'pendingQty2', header: 'Pnd Qty2', isNumeric: true, w: 10 },
                { id: 'pendingQty3', header: 'Pnd Qty3', isNumeric: true, w: 10 },
                { id: 'netAmount', header: 'Net Amt', isNumeric: true, w: 14 },
                { id: 'pendingAmt', header: 'Pending Amt', isNumeric: true, w: 14 },
                { id: 'challanDocNo', header: 'Ch No', isNumeric: false, w: 14 },
                { id: 'challanDate', header: 'Ch Date', isNumeric: false, w: 20 },
                { id: 'challanDaybook', header: 'Ch Daybook', isNumeric: false, w: 20 },
                { id: 'challanCompany', header: 'Ch Company', isNumeric: false, w: 20 },
            ];

            const totalCols = COLUMNS.length;
            const QTY_IDS = new Set(['qty1', 'qty2', 'qty3']);
            const DEL_QTY_IDS = new Set(['deliveredQty1', 'deliveredQty2', 'deliveredQty3']);
            const PND_QTY_IDS = new Set(['pendingQty1', 'pendingQty2', 'pendingQty3']);
            const DATE_IDS = new Set(['transactionAt']);
            // Columns to keep visible on extra-challan rows (pIndex > 0)
            const CHALLAN_ONLY_COLS = new Set(['deliveredQty1', 'deliveredQty2', 'deliveredQty3',
                'challanDocNo', 'challanDate', 'challanDaybook', 'challanCompany']);

            const makeHeader = (col) => {
                const id = col.id;
                let fontColor = '374151';
                if (QTY_IDS.has(id)) fontColor = '2563eb';
                else if (DEL_QTY_IDS.has(id)) fontColor = '16a34a';
                else if (PND_QTY_IDS.has(id)) fontColor = 'ef4444';
                return {
                    v: col.header, t: 's',
                    s: {
                        font: { bold: true, name: 'Arial', sz: 9, color: { rgb: fontColor } },
                        fill: { fgColor: { rgb: 'D3D3D3' } },
                        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                        border: {
                            top: { style: 'thin', color: { rgb: 'AAAAAA' } },
                            bottom: { style: 'medium', color: { rgb: '888888' } },
                            left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                            right: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        },
                    },
                };
            };

            // rowType:
            //   'merged'        — white  (tx + first item + first challan)
            //   'lineitem'      — blue   (subsequent item + its first challan)
            //   'challan_extra' — green  (extra challans for same line item)
            const makeCell = (value, col, rowType) => {
                const id = col.id;
                const isQty = QTY_IDS.has(id);
                const isDelQty = DEL_QTY_IDS.has(id);
                const isPndQty = PND_QTY_IDS.has(id);
                const isNetAmt = id === 'netAmount';
                const isPndAmt = id === 'pendingAmt';
                const isDate = DATE_IDS.has(id);
                const isChallanField = ['challanDocNo', 'challanDate', 'challanDaybook', 'challanCompany'].includes(id);
                const isEmpty = value === '' || value === null || value === undefined;

                let fillColor = 'FFFFFF';
                if (rowType === 'lineitem') fillColor = 'EFF6FF'; // light blue
                else if (rowType === 'challan_extra') fillColor = 'F0FDF4'; // light green

                const bdr = {
                    bottom: { style: 'hair', color: { rgb: 'DDDDDD' } },
                    right: { style: 'hair', color: { rgb: 'DDDDDD' } },
                };

                if (isEmpty) {
                    return { v: '', t: 's', s: { font: { name: 'Arial', sz: 9, color: { rgb: 'CCCCCC' } }, fill: { fgColor: { rgb: fillColor } }, alignment: { horizontal: 'center' }, border: bdr } };
                }
                if (isDate) {
                    if (value instanceof Date && !isNaN(value)) {
                        return { v: value, t: 'd', z: 'dd-mm-yyyy', s: { font: { name: 'Arial', sz: 9, color: { rgb: '374151' } }, fill: { fgColor: { rgb: fillColor } }, alignment: { horizontal: 'center' }, border: bdr } };
                    }
                    return { v: String(value), t: 's', s: { font: { name: 'Arial', sz: 9, color: { rgb: '374151' } }, fill: { fgColor: { rgb: fillColor } }, alignment: { horizontal: 'center' }, border: bdr } };
                }
                if (col.isNumeric) {
                    const num = Number(value);
                    if (!isNaN(num)) {
                        let fontColor = '374151';
                        if (isQty) fontColor = '2563eb';
                        else if (isDelQty) fontColor = '16a34a';
                        else if (isPndQty) fontColor = 'ef4444';
                        else if (isPndAmt) fontColor = 'f97316';
                        return { v: num, t: 'n', s: { numFmt: (isQty || isDelQty || isPndQty) ? '#,##0' : '#,##0.00', font: { name: 'Arial', sz: 9, bold: isQty || isDelQty || isPndQty || isNetAmt || isPndAmt, color: { rgb: fontColor } }, fill: { fgColor: { rgb: fillColor } }, alignment: { horizontal: 'right' }, border: bdr } };
                    }
                }
                return { v: String(value), t: 's', s: { font: { name: 'Arial', sz: 9, bold: isChallanField, color: { rgb: isChallanField ? 'BF5000' : '374151' } }, fill: { fgColor: { rgb: fillColor } }, alignment: { horizontal: 'left' }, border: bdr } };
            };

            const allRows = [];

            const processTransactions = (transactions) => {
                (transactions || []).forEach((t) => {
                    if (!t) return;
                    const lis = t.salesOrderLineItems || [];

                    if (lis.length === 0) {
                        const txRow = {};
                        COLUMNS.forEach(col => { txRow[col.id] = ''; });
                        txRow.transactionAt = parseDate(t.transactionAt || t.rawTransactionAt) || '-';
                        txRow.docno = t.docno || '-';
                        txRow.accountname = t.accountname || '-';
                        txRow.mobile = t.mobile || '-';
                        txRow.daybookname = t.daybookname || '-';
                        txRow.salesmanname = t.salesmanname || '-';
                        txRow.notes = t.notes || '-';
                        txRow.netAmount = Number(t.netAmount || 0);
                        txRow.pendingAmt = Number(t.pendingAmt || 0);
                        allRows.push({ type: 'merged', data: txRow });
                        return;
                    }

                    lis.forEach((li, liIndex) => {
                        const processedList = li.processedList || [];

                        const baseRow = {};
                        COLUMNS.forEach(col => { baseRow[col.id] = ''; });

                        if (liIndex === 0) {
                            baseRow.transactionAt = parseDate(t.transactionAt || t.rawTransactionAt) || '-';
                            baseRow.docno = t.docno || '-';
                            baseRow.accountname = t.accountname || '-';
                            baseRow.mobile = t.mobile || '-';
                            baseRow.daybookname = t.daybookname || '-';
                            baseRow.salesmanname = t.salesmanname || '-';
                            baseRow.notes = t.notes || '-';
                            baseRow.netAmount = Number(t.netAmount || 0);
                            baseRow.pendingAmt = Number(t.pendingAmt || 0);
                        }

                        baseRow.itemName = li.itemName || '-';
                        baseRow.itemRate = Number(li.itemRate || 0);
                        baseRow.qty1 = Number(li.qty1 || 0);
                        baseRow.qty2 = Number(li.qty2 || 0);
                        baseRow.qty3 = Number(li.qty3 || 0);
                        baseRow.pendingQty1 = Number(li.pendingQty1 || 0);
                        baseRow.pendingQty2 = Number(li.pendingQty2 || 0);
                        baseRow.pendingQty3 = Number(li.pendingQty3 || 0);

                        if (processedList.length === 0) {
                            allRows.push({ type: liIndex === 0 ? 'merged' : 'lineitem', data: { ...baseRow } });
                            return;
                        }

                        // ✅ Each challan = separate row
                        processedList.forEach((p, pIndex) => {
                            const row = { ...baseRow };

                            // 2nd+ challan: blank all non-challan cols
                            if (pIndex > 0) {
                                COLUMNS.forEach(col => {
                                    if (!CHALLAN_ONLY_COLS.has(col.id)) row[col.id] = '';
                                });
                            }

                            row.deliveredQty1 = Number(p.qty1 || 0);
                            row.deliveredQty2 = Number(p.qty2 || 0);
                            row.deliveredQty3 = Number(p.qty3 || 0);
                            row.challanDocNo = p.docNo || '-';
                            row.challanDate = fmtDateStr(p.transactionAt) || '-';
                            row.challanDaybook = p.daybookName || '-';
                            row.challanCompany = p.companyName || '-';

                            let rowType = 'challan_extra';
                            if (pIndex === 0) rowType = liIndex === 0 ? 'merged' : 'lineitem';

                            allRows.push({ type: rowType, data: row });
                        });
                    });
                });
            };

            salesReport.forEach((cd) => {
                if (!cd) return;
                if (reportType === REPORT_TYPE.party || reportType === 'party')
                    cd.data?.forEach(acc => processTransactions(acc.data));
                else
                    cd.data?.forEach(dg => processTransactions(dg.data));
            });

            if (!allRows.length) { alert('No transaction data found'); return; }

            const wsData = [];

            const titleRow = Array(totalCols).fill({ v: '', t: 's', s: {} });
            titleRow[0] = {
                v: `Sales Order Detail — ${fmtLabel}`, t: 's',
                s: { font: { bold: true, name: 'Arial', sz: 14, color: { rgb: '1A237E' } }, fill: { fgColor: { rgb: 'E8EAF6' } }, alignment: { horizontal: 'center', vertical: 'center' } },
            };
            wsData.push(titleRow);

            const metaRow = Array(totalCols).fill({ v: '', t: 's', s: {} });
            const companies = [...new Set(salesReport.map(c => c.companyName).filter(Boolean))];
            metaRow[0] = {
                v: `Companies: ${companies.join(', ')}   |   Generated: ${moment().format('DD-MM-YYYY HH:mm:ss')}`, t: 's',
                s: { font: { name: 'Arial', sz: 9, color: { rgb: '555555' } }, fill: { fgColor: { rgb: 'F5F5F5' } }, alignment: { horizontal: 'center', vertical: 'center' } },
            };
            wsData.push(metaRow);
            wsData.push(Array(totalCols).fill({ v: '', t: 's', s: {} }));
            wsData.push(COLUMNS.map(col => makeHeader(col)));
            allRows.forEach((row) => {
                wsData.push(COLUMNS.map(col => makeCell(row.data[col.id], col, row.type)));
            });

            const ws = XLSX.utils.aoa_to_sheet(wsData);
            ws['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } },
            ];
            ws['!rows'] = [{ hpt: 30 }, { hpt: 18 }, { hpt: 6 }, { hpt: 26 }];
            ws['!cols'] = COLUMNS.map(col => ({ wch: col.w || 12 }));

            XLSX.utils.book_append_sheet(wb, ws, 'Sales Order Details');
            XLSX.writeFile(wb, `${fmtLabel}_SO_Detail_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);

        } catch (err) {
            console.error('Excel export error:', err);
            alert('Error generating Excel. Check console for details.');
        }
    }, [salesReport, reportType, dynamicValues, dynamicOptions, staticReportTypeOptions]);

    const downloadPDF = useCallback(() => {
        if (!salesReport || salesReport.length === 0) {
            alert('No data available to export');
            return;
        }

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
        const PAGE_W = doc.internal.pageSize.getWidth();
        const PAGE_H = doc.internal.pageSize.getHeight();
        const MARGIN = 10;
        const USABLE_W = PAGE_W - MARGIN * 2;

        const parseDate = (v) => {
            if (!v) return null;
            if (v instanceof Date && !isNaN(v)) return v;
            if (typeof v === 'string') {
                if (/^\d{2}-\d{2}-\d{4}$/.test(v)) { const [d, m, y] = v.split('-'); return new Date(+y, +m - 1, +d); }
                if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) { const [d, m, y] = v.split('/'); return new Date(+y, +m - 1, +d); }
                if (/^\d{4}-\d{2}-\d{2}/.test(v)) { const dt = new Date(v); if (!isNaN(dt)) return dt; }
            }
            if (!isNaN(v)) { const ts = +v; const dt = ts > 9999999999 ? new Date(ts) : new Date(ts * 1000); if (!isNaN(dt)) return dt; }
            return null;
        };

        const fmtDate = (v) => { if (!v) return '-'; const d = parseDate(v); return d ? moment(d).format('DD-MM-YY') : String(v); };
        const n = (v) => Number(v || 0).toLocaleString();

        const isDynamic = dynamicValues?.includes(reportType);
        const selFmt = isDynamic ? dynamicOptions?.find(o => o.value === reportType) : null;
        const defName = selFmt?.formatData?.defName;
        const fmtLabel = isDynamic
            ? (dynamicOptions?.find(o => o.value === reportType)?.label || reportType)
            : (staticReportTypeOptions?.find(o => o.value === reportType)?.label || reportType);

        const COLUMNS = [
            { id: 'transactionAt', header: 'Date', w: 10, num: false },
            { id: 'docno', header: 'Doc No', w: 5, num: false },
            { id: 'accountname', header: 'Party', w: 16, num: false },
            { id: 'mobile', header: 'Mobile', w: 9, num: false },
            { id: 'itemName', header: 'Item Name', w: 16, num: false },
            { id: 'itemRate', header: 'Rate', w: 7, num: true },
            { id: 'daybookname', header: 'Daybook', w: 11, num: false },
            { id: 'salesmanname', header: 'Salesman', w: 9, num: false },
            { id: 'notes', header: 'Notes', w: 8, num: false },
            { id: 'qty1', header: 'Ord\nQty1', w: 6, num: true },
            { id: 'qty2', header: 'Ord\nQty2', w: 6, num: true },
            { id: 'qty3', header: 'Ord\nQty3', w: 6, num: true },
            { id: 'deliveredQty1', header: 'Del\nQty1', w: 6, num: true },
            { id: 'deliveredQty2', header: 'Del\nQty2', w: 6, num: true },
            { id: 'deliveredQty3', header: 'Del\nQty3', w: 6, num: true },
            { id: 'pendingQty1', header: 'Pnd\nQty1', w: 6, num: true },
            { id: 'pendingQty2', header: 'Pnd\nQty2', w: 6, num: true },
            { id: 'pendingQty3', header: 'Pnd\nQty3', w: 6, num: true },
            { id: 'netAmount', header: 'Net Amt', w: 10, num: true },
            { id: 'pendingAmt', header: 'Pnd\nAmt', w: 10, num: true },
            { id: 'challanDocNo', header: 'Ch.No', w: 9, num: false },
            { id: 'challanDate', header: 'Ch.Date', w: 12, num: false },
            { id: 'challanDaybook', header: 'Ch.Daybook', w: 11, num: false },
            { id: 'challanCompany', header: 'Ch.Company', w: 11, num: false },
        ];

        const totalCols = COLUMNS.length;
        const totalW = COLUMNS.reduce((s, c) => s + c.w, 0);
        const colW = (c) => parseFloat(((c.w / totalW) * USABLE_W).toFixed(2));

        const QTY_IDS = new Set(['qty1', 'qty2', 'qty3']);
        const DEL_QTY_IDS = new Set(['deliveredQty1', 'deliveredQty2', 'deliveredQty3']);
        const PND_QTY_IDS = new Set(['pendingQty1', 'pendingQty2', 'pendingQty3']);
        const CHALLAN_ONLY_COLS = new Set(['deliveredQty1', 'deliveredQty2', 'deliveredQty3',
            'challanDocNo', 'challanDate', 'challanDaybook', 'challanCompany']);

        const columnStyles = {};
        COLUMNS.forEach((col, i) => {
            columnStyles[i] = { cellWidth: colW(col), halign: col.num ? 'right' : 'left', valign: 'top', cellPadding: { top: 1, bottom: 1, left: 1, right: 1 }, fontSize: 6.5, overflow: 'linebreak', lineWidth: 0 };
        });

        const data = [];
        const sumQty = (txs) => txs.reduce((s, t) => s + (t.salesOrderLineItems || []).reduce((sum, li) => sum + Number(li.qty1 || 0), 0), 0);
        const sumAmt = (txs) => txs.reduce((s, t) => s + Number(t.netAmount || 0), 0);
        const extractAll = (cd) => { const r = []; (cd.data || []).forEach(g => (g.data || []).forEach(t => r.push(t))); return r; };

        const addGroupHdr = (text, level = 0) => data.push([{
            content: text, colSpan: totalCols,
            styles: { fillColor: level === 0 ? [225, 225, 225] : [240, 240, 240], fontStyle: 'bold', fontSize: level === 0 ? 7.5 : 7, cellPadding: { top: 2, bottom: 2, left: level === 0 ? 3 : 8, right: 3 }, halign: 'left', valign: 'middle', lineWidth: { bottom: 0.3 }, lineColor: [180, 180, 180] },
        }]);

        // rowType:
        //   'merged'        — white  (tx + first item + first challan)
        //   'lineitem'      — blue   (subsequent item + its first challan)
        //   'challan_extra' — green  (extra challans for same line item)
        const buildCell = (value, col, rowType) => {
            const colId = col.id;
            const isEmpty = value === '' || value === undefined || value === null || value === '-';
            const isQty = QTY_IDS.has(colId);
            const isDelQty = DEL_QTY_IDS.has(colId);
            const isPndQty = PND_QTY_IDS.has(colId);
            const isPndAmt = colId === 'pendingAmt';
            const isChallanInfo = ['challanDocNo', 'challanDate', 'challanDaybook', 'challanCompany'].includes(colId);

            let fillColor;
            if (rowType === 'lineitem') fillColor = [239, 246, 255]; // blue
            else if (rowType === 'challan_extra') fillColor = [240, 253, 244]; // green
            else fillColor = [255, 255, 255]; // white

            let textColor = [55, 65, 81];
            if (!isEmpty) {
                if (isQty) textColor = [37, 99, 235];
                else if (isDelQty) textColor = [22, 163, 74];
                else if (isPndQty) textColor = [239, 68, 68];
                else if (isPndAmt) textColor = [249, 115, 22];
                else if (isChallanInfo) textColor = [159, 80, 0];
            } else { textColor = [200, 200, 200]; }

            return {
                content: isEmpty ? '-' : String(value),
                styles: { fillColor, textColor, fontStyle: (isQty || isDelQty || isPndQty || isPndAmt || isChallanInfo) ? 'bold' : 'normal', halign: isEmpty ? 'center' : col.num ? 'right' : 'left', lineWidth: 0 },
            };
        };

        const buildHeader = () => COLUMNS.map((col) => {
            const id = col.id; const fill = [220, 220, 220]; let text = [55, 65, 81];
            if (QTY_IDS.has(id)) text = [37, 99, 235];
            else if (DEL_QTY_IDS.has(id)) text = [22, 163, 74];
            else if (PND_QTY_IDS.has(id)) text = [239, 68, 68];
            return { content: col.header, styles: { fillColor: fill, textColor: text, fontStyle: 'bold', fontSize: 6, halign: 'center', valign: 'middle', cellPadding: { top: 1.5, bottom: 1.5, left: 1, right: 1 }, overflow: 'linebreak', lineWidth: { bottom: 0.5 }, lineColor: [100, 100, 100] } };
        });

        const addRows = (txs) => {
            txs.forEach((t) => {
                if (!t) return;
                const lis = t.salesOrderLineItems || [];
                const blank = () => { const r = {}; COLUMNS.forEach(c => { r[c.id] = ''; }); return r; };

                if (lis.length === 0) {
                    const txData = blank();
                    txData.transactionAt = fmtDate(t.transactionAt || t.rawTransactionAt);
                    txData.docno = String(t.docno || '-');
                    txData.accountname = t.accountname || '-';
                    txData.mobile = t.mobile || '-';
                    txData.daybookname = t.daybookname || '-';
                    txData.salesmanname = t.salesmanname || '-';
                    txData.notes = t.notes || '-';
                    txData.netAmount = Number(t.netAmount || 0) > 0 ? n(Number(t.netAmount)) : '-';
                    txData.pendingAmt = Number(t.pendingAmt || 0) > 0 ? n(Number(t.pendingAmt)) : '-';
                    data.push(COLUMNS.map(col => buildCell(txData[col.id], col, 'merged')));
                    return;
                }

                lis.forEach((li, liIndex) => {
                    const processedList = li.processedList || [];
                    const baseData = blank();

                    if (liIndex === 0) {
                        baseData.transactionAt = fmtDate(t.transactionAt || t.rawTransactionAt);
                        baseData.docno = String(t.docno || '-');
                        baseData.accountname = t.accountname || '-';
                        baseData.mobile = t.mobile || '-';
                        baseData.daybookname = t.daybookname || '-';
                        baseData.salesmanname = t.salesmanname || '-';
                        baseData.notes = t.notes || '-';
                        baseData.netAmount = Number(t.netAmount || 0) > 0 ? n(Number(t.netAmount)) : '-';
                        baseData.pendingAmt = Number(t.pendingAmt || 0) > 0 ? n(Number(t.pendingAmt)) : '-';
                    }

                    baseData.itemName = li.itemName || '-';
                    baseData.itemRate = Number(li.itemRate || 0) > 0 ? n(Number(li.itemRate)) : '-';
                    baseData.qty1 = Number(li.qty1 || 0) > 0 ? n(Number(li.qty1)) : '-';
                    baseData.qty2 = Number(li.qty2 || 0) > 0 ? n(Number(li.qty2)) : '-';
                    baseData.qty3 = Number(li.qty3 || 0) > 0 ? n(Number(li.qty3)) : '-';
                    baseData.pendingQty1 = Number(li.pendingQty1 || 0) > 0 ? n(Number(li.pendingQty1)) : '-';
                    baseData.pendingQty2 = Number(li.pendingQty2 || 0) > 0 ? n(Number(li.pendingQty2)) : '-';
                    baseData.pendingQty3 = Number(li.pendingQty3 || 0) > 0 ? n(Number(li.pendingQty3)) : '-';

                    if (processedList.length === 0) {
                        data.push(COLUMNS.map(col => buildCell(baseData[col.id], col, liIndex === 0 ? 'merged' : 'lineitem')));
                        return;
                    }

                    // ✅ Each challan = separate row
                    processedList.forEach((p, pIndex) => {
                        const rowData = { ...baseData };

                        // 2nd+ challan: blank all non-challan cols
                        if (pIndex > 0) {
                            COLUMNS.forEach(col => {
                                if (!CHALLAN_ONLY_COLS.has(col.id)) rowData[col.id] = '';
                            });
                        }

                        const dQty1 = Number(p.qty1 || 0);
                        const dQty2 = Number(p.qty2 || 0);
                        const dQty3 = Number(p.qty3 || 0);
                        rowData.deliveredQty1 = dQty1 > 0 ? n(dQty1) : '-';
                        rowData.deliveredQty2 = dQty2 > 0 ? n(dQty2) : '-';
                        rowData.deliveredQty3 = dQty3 > 0 ? n(dQty3) : '-';
                        rowData.challanDocNo = p.docNo || '-';
                        rowData.challanDate = fmtDate(p.transactionAt) || '-';
                        rowData.challanDaybook = p.daybookName || '-';
                        rowData.challanCompany = p.companyName || '-';

                        let rowType = 'challan_extra';
                        if (pIndex === 0) rowType = liIndex === 0 ? 'merged' : 'lineitem';

                        data.push(COLUMNS.map(col => buildCell(rowData[col.id], col, rowType)));
                    });
                });
            });
        };

        doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(0, 0, 0);
        doc.text(`Sales Order Detail — ${fmtLabel}`, PAGE_W / 2, 10, { align: 'center' });
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(60, 60, 60);
        const cos = [...new Set(salesReport.map(c => c.companyName).filter(Boolean))];
        doc.text(`Companies: ${cos.join(', ')}   |   ${moment().format('DD-MM-YYYY HH:mm:ss')}`, PAGE_W / 2, 16, { align: 'center' });
        doc.text(`Date: ${fmtDate(dateRange.fromDate)} TO ${fmtDate(dateRange.toDate)}   |   ${fmtLabel}`, PAGE_W / 2, 21, { align: 'center' });
        doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.3);
        doc.line(MARGIN, 23, PAGE_W - MARGIN, 23);

        salesReport.forEach((cd) => {
            const cn = cd.companyName || 'Company';
            if (isDynamic) {
                const all = extractAll(cd);
                addGroupHdr(`${cn}  |  Orders: ${all.length}  |  Qty: ${n(sumQty(all))}  |  Net Amt: ${n(sumAmt(all))}`, 0);
                if (defName === 'SalesOrderDetailParty') {
                    const byP = {};
                    all.forEach(t => { const k = t.accountname || 'Unknown'; (byP[k] = byP[k] || []).push(t); });
                    Object.entries(byP).forEach(([name, txs]) => { addGroupHdr(`${name}  |  Qty: ${n(sumQty(txs))}  |  Amt: ${n(sumAmt(txs))}`, 1); addRows(txs); });
                } else if (defName === 'SalesOrderDetailDate') {
                    const byD = {};
                    all.forEach(t => { const k = fmtDate(t.transactionAt || t.rawTransactionAt) || 'No Date'; (byD[k] = byD[k] || []).push(t); });
                    Object.entries(byD).forEach(([date, txs]) => { addGroupHdr(`${date}  |  Qty: ${n(sumQty(txs))}  |  Amt: ${n(sumAmt(txs))}`, 1); addRows(txs); });
                } else { addRows(all); }
            } else if (reportType === REPORT_TYPE.party || reportType === 'party') {
                const all = cd.data?.flatMap(a => a.data || []) || [];
                addGroupHdr(`${cn}  |  Qty: ${n(sumQty(all))}  |  Net Amt: ${n(sumAmt(all))}`, 0);
                cd.data?.forEach((acc) => { const txs = acc.data || []; addGroupHdr(`${acc.accountName || ''}  |  Qty: ${n(sumQty(txs))}  |  Amt: ${n(sumAmt(txs))}`, 1); addRows(txs); });
            } else {
                const all = extractAll(cd);
                addGroupHdr(`${cn}  |  Qty: ${n(sumQty(all))}  |  Net Amt: ${n(sumAmt(all))}`, 0);
                const byD = {};
                (cd.data || []).forEach(g => { const k = fmtDate(g.date) || 'No Date'; (byD[k] = byD[k] || []).push(...(g.data || [])); });
                Object.entries(byD).forEach(([date, txs]) => { addGroupHdr(`${date}  |  Qty: ${n(sumQty(txs))}  |  Amt: ${n(sumAmt(txs))}`, 1); addRows(txs); });
            }
            data.push([{ content: '', colSpan: totalCols, styles: { cellPadding: 1, lineWidth: { bottom: 0.4 }, lineColor: [160, 160, 160] } }]);
        });

        if (!data.length) { alert('No data found'); return; }

        doc.autoTable({
            head: [buildHeader()], body: data, startY: 25,
            margin: { left: MARGIN, right: MARGIN }, tableWidth: USABLE_W,
            styles: { font: 'helvetica', fontSize: 6.5, cellPadding: { top: 1, bottom: 1, left: 1, right: 1 }, overflow: 'linebreak', lineWidth: 0, valign: 'top', minCellHeight: 5 },
            headStyles: { fillColor: [220, 220, 220], fontStyle: 'bold', fontSize: 6, halign: 'center', valign: 'middle', lineWidth: { bottom: 0.5 }, lineColor: [100, 100, 100], minCellHeight: 8 },
            bodyStyles: { lineWidth: 0, valign: 'top' },
            columnStyles,
            willDrawCell: () => { doc.setCharSpace(0); },
            didDrawPage: (hd) => {
                doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(130, 130, 130);
                doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.2);
                doc.line(MARGIN, PAGE_H - 8, PAGE_W - MARGIN, PAGE_H - 8);
                doc.text(`${fmtLabel} — Sales Order Detail`, MARGIN, PAGE_H - 5);
                doc.text(`Page ${hd.pageNumber} of ${doc.internal.getNumberOfPages()}`, PAGE_W - MARGIN, PAGE_H - 5, { align: 'right' });
            },
        });

        doc.save(`${fmtLabel}_SO_Detail_${moment().format('YYYYMMDD_HHmmss')}.pdf`);

    }, [salesReport, dateRange.fromDate, dateRange.toDate, reportType, dynamicValues, dynamicOptions, staticReportTypeOptions]);

    // const deletePurchaseReturn = () => {
    //    deleteRecord(viewDetail?.id, deleteSaleOrder, dispatch);
    //    setIsOpenPurchaseReturnSingleView(false);
    // };

    const deletePurchaseReturn = () => {
        const selectedDaybook = daybookData.find((db) => db.id === viewDetail?.daybookId);
        console.log('viewDetail data', viewDetail, selectedDaybook);
        if (selectedDaybook && selectedDaybook?.askPasswordOnDelete) {
            setDataId({ id: viewDetail?.id, daybookId: viewDetail.daybookId, accountId: viewDetail.accountId });
            setIsModalOpen(true);
            setIsOpenPurchaseReturnSingleView(false);
        } else {
            deleteRecordForSales(
                viewDetail?.id,
                viewDetail?.daybookId,
                viewDetail?.accountId,
                deleteSaleOrder,
                dispatch,
                storedCompanyId,
                userGlobalSettingsData,
            );
            setIsOpenPurchaseReturnSingleView(false);
        }
        // deleteRecordForSales(
        //    viewDetail?.id,
        //    viewDetail?.daybookId,
        //    viewDetail?.accountId,
        //    deleteSaleOrder,
        //    dispatch,
        //    storedCompanyId,
        //    userGlobalSettingsData,
        // );
        // setIsOpenPurchaseReturnSingleView(false);
    };

    const handleSalesDetailsClose = () => {
        setIsOpenPurchaseReturnSingleView();
    };

    useEffect(() => {
        const calculateHeight = () => {
            const windowHeight = window.innerHeight;
            const headerHeight = 250;
            const cardMargin = 15;
            const padding = 32;

            const availableHeight = windowHeight - headerHeight - cardMargin - padding;
            setContainerHeight(Math.max(availableHeight, 400));
        };

        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return () => window.removeEventListener('resize', calculateHeight);
    }, []);

    const getDeliveryStats = useCallback((lineItem) => {
        if (!lineItem) return {
            ordered1: 0, delivered1: 0, pending1: 0,
            ordered2: 0, delivered2: 0, pending2: 0,
            ordered3: 0, delivered3: 0, pending3: 0,
            pct: 0,
        };

        // ── Ordered ──────────────────────────────
        const ordered1 = Number(lineItem.qty1 || 0);
        const ordered2 = Number(lineItem.qty2 || 0);
        const ordered3 = Number(lineItem.qty3 || 0);

        // ── Delivered (sum from processedList) ───
        const delivered1 = (lineItem.processedList || []).reduce(
            (sum, p) => sum + Number(p.qty1 || 0), 0
        );
        const delivered2 = (lineItem.processedList || []).reduce(
            (sum, p) => sum + Number(p.qty2 || 0), 0
        );
        const delivered3 = (lineItem.processedList || []).reduce(
            (sum, p) => sum + Number(p.qty3 || 0), 0
        );

        // ── Pending (backend pendingQty or calculate) ─
        const pending1 = Number(lineItem.pendingQty1 || 0) || Math.max(0, ordered1 - delivered1);
        const pending2 = Number(lineItem.pendingQty2 || 0) || Math.max(0, ordered2 - delivered2);
        const pending3 = Number(lineItem.pendingQty3 || 0) || Math.max(0, ordered3 - delivered3);

        // ── Overall % based on qty1 (primary) ────
        const totalOrdered = ordered1 + ordered2 + ordered3;
        const totalDelivered = delivered1 + delivered2 + delivered3;
        const pct = totalOrdered > 0
            ? Math.min(100, Math.round((totalDelivered / totalOrdered) * 100))
            : 0;

        return {
            ordered1, delivered1, pending1,
            ordered2, delivered2, pending2,
            ordered3, delivered3, pending3,
            pct,
        };
    }, []);

    const calculateGrandTotals = useCallback(() => {
        if (!salesReport || salesReport.length === 0) return { totalQty: 0, totalAmt: 0 };

        let grandTotalQty = 0;
        let grandTotalAmt = 0;

        salesReport.forEach((companyData) => {
            if (isStaticReportType) {
                if (reportType === REPORT_TYPE.party || reportType === 'party') {
                    // For party report type
                    const companyQty = companyData.data?.reduce((sum, account) => sum + (account.totalQty1 || 0), 0) || 0;
                    const companyAmt = companyData.data?.reduce((sum, account) => sum + (account.totalNetAmt || 0), 0) || 0;
                    grandTotalQty += companyQty;
                    grandTotalAmt += companyAmt;
                } else {
                    // For date/broker report type
                    const itemData = companyData.data?.flatMap((item) => item.data || []) || [];
                    const companyQty = itemData.reduce((sum, trans) => sum + (Number(trans.totalQty1) || 0), 0);
                    const companyAmt = itemData.reduce((sum, trans) => sum + (Number(trans.netAmount) || 0), 0);
                    grandTotalQty += companyQty;
                    grandTotalAmt += companyAmt;
                }
            } else {
                // For dynamic formats
                const transactions = companyData.data || [];
                const companyQty = transactions.reduce((sum, trans) => {
                    if (formatType === 'with_item_detail' && trans.purchaseLineItems) {
                        return sum + trans.purchaseLineItems.reduce((itemSum, item) => itemSum + (Number(item.qty1) || 0), 0);
                    }
                    return sum + (Number(trans.totalQty1) || 0);
                }, 0);

                const companyAmt = transactions.reduce((sum, trans) => {
                    if (formatType === 'with_item_detail' && trans.purchaseLineItems) {
                        return sum + trans.purchaseLineItems.reduce((itemSum, item) => itemSum + (Number(item.netAmt) || 0), 0);
                    }
                    return sum + (Number(trans.netAmount) || 0);
                }, 0);

                grandTotalQty += companyQty;
                grandTotalAmt += companyAmt;
            }
        });

        return { grandTotalQty, grandTotalAmt };
    }, [salesReport, reportType, formatType, isStaticReportType]);

    const flattenHierarchicalData = useCallback(() => {
        if (!salesReport || salesReport.length === 0) return [];

        const flatItems = [];

        if (isStaticReportType) {
            if (reportType === REPORT_TYPE.party || reportType === 'party') {
                // Static party format
                salesReport.forEach((companyData, companyIndex) => {

                    if (companyFilter.length > 1) {
                        const { grandTotalQty, grandTotalAmt } = calculateGrandTotals();
                        flatItems.push({
                            type: 'grand-total',
                            data: { totalQty: grandTotalQty, totalAmt: grandTotalAmt },
                            id: 'grand-total',
                            depth: 0,
                        });
                    }
                    flatItems.push({
                        type: 'company-header',
                        data: companyData,
                        id: `company-${companyData.companyId || companyIndex}`,
                        companyId: companyData.companyId,
                        depth: 0,
                    });

                    if (expandedItems[`company-${companyData.companyId}`] && companyData.data) {
                        companyData.data.forEach((accountData, accountIndex) => {
                            flatItems.push({
                                type: 'account-header',
                                data: accountData,
                                id: `account-${accountData.accountId || accountIndex}`,
                                companyId: companyData.companyId,
                                accountId: accountData.accountId,
                                depth: 1,
                            });

                            if (expandedItems[`account-${accountData.accountId}`] && accountData.data) {
                                flatItems.push({
                                    type: 'column-headers',
                                    data: null,
                                    id: `headers-${accountData.accountId}`,
                                    companyId: companyData.companyId,
                                    accountId: accountData.accountId,
                                    depth: 2,
                                });

                                accountData.data.forEach((transaction, transIndex) => {
                                    const hasMultipleItems =
                                        formatType === 'with_item_detail' &&
                                        transaction.salesOrderLineItems &&
                                        Array.isArray(transaction.salesOrderLineItems) &&
                                        transaction.salesOrderLineItems.length > 1;

                                    // Only first lineItem's processedList for transaction-level expand
                                    const firstLineItem = transaction.salesOrderLineItems?.[0];
                                    const firstLineItemHasProcessed =
                                        firstLineItem?.processedList && firstLineItem.processedList.length > 0;

                                    flatItems.push({
                                        type: 'transaction-row',
                                        data: transaction,
                                        id: `trans-${transaction.id || transIndex}`,
                                        companyId: companyData.companyId,
                                        accountId: accountData.accountId,
                                        depth: 2,
                                        hasMultipleItems: hasMultipleItems,
                                        hasProcessedItems: firstLineItemHasProcessed,
                                    });

                                    // Processed rows for FIRST lineItem (transaction-level expand)
                                    if (firstLineItemHasProcessed && expandedItems[`items-${transaction.id}`]) {
                                        firstLineItem.processedList.forEach((processedItem, procIndex) => {
                                            flatItems.push({
                                                type: 'processed-row',
                                                data: processedItem,
                                                parentTransaction: transaction,
                                                parentLineItem: firstLineItem,
                                                id: `processed-${transaction.id}-0-${procIndex}`,
                                                companyId: companyData.companyId,
                                                accountId: accountData.accountId,
                                                depth: 4,
                                            });
                                        });
                                    }

                                    // Additional line items (2nd, 3rd, ...)
                                    if (hasMultipleItems) {
                                        transaction.salesOrderLineItems.slice(1).forEach((lineItem, itemIndex) => {
                                            const lineItemHasProcessed =
                                                lineItem.processedList && lineItem.processedList.length > 0;

                                            flatItems.push({
                                                type: 'item-row',
                                                data: lineItem,
                                                parentTransaction: transaction,
                                                id: `item-${itemIndex + 1}-${transaction.id}`,
                                                companyId: companyData.companyId,
                                                accountId: accountData.accountId,
                                                depth: 3,
                                                hasProcessedItems: lineItemHasProcessed, // per-lineItem flag
                                            });

                                            // Processed rows for THIS specific lineItem
                                            if (lineItemHasProcessed && expandedItems[`lineitem-${lineItem.id}`]) {
                                                lineItem.processedList.forEach((processedItem, procIndex) => {
                                                    flatItems.push({
                                                        type: 'processed-row',
                                                        data: processedItem,
                                                        parentTransaction: transaction,
                                                        parentLineItem: lineItem,
                                                        id: `processed-lineitem-${lineItem.id}-${procIndex}`,
                                                        companyId: companyData.companyId,
                                                        accountId: accountData.accountId,
                                                        depth: 4,
                                                    });
                                                });
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
            } else {

                if (companyFilter.length > 1) {
                    const { grandTotalQty, grandTotalAmt } = calculateGrandTotals();
                    flatItems.push({
                        type: 'grand-total',
                        data: { totalQty: grandTotalQty, totalAmt: grandTotalAmt },
                        id: 'grand-total',
                        depth: 0,
                    });
                }
                // Static date format
                salesReport.forEach((companyData, companyIndex) => {
                    flatItems.push({
                        type: 'company-header',
                        data: companyData,
                        id: `company-${companyData.companyId || companyIndex}`,
                        companyId: companyData.companyId,
                        depth: 0,
                    });

                    const itemData = companyData.data?.flatMap((item) => item.data || []) || [];
                    const groupedByDate = itemData.reduce((acc, item) => {
                        const date = item.transactionAt || 'No Date';
                        if (!acc[date]) acc[date] = [];
                        acc[date].push(item);
                        return acc;
                    }, {});

                    flatItems.push({
                        type: 'column-headers',
                        data: null,
                        id: `headers-company-${companyData.companyId}`,
                        companyId: companyData.companyId,
                        depth: 1,
                    });

                    Object.entries(groupedByDate).forEach(([date, dateTransactions], dateIndex) => {
                        flatItems.push({
                            type: 'date-header',
                            data: { date, transactions: dateTransactions },
                            id: `date-${date}-${dateIndex}`,
                            companyId: companyData.companyId,
                            depth: 1,
                        });

                        dateTransactions.forEach((transaction, transIndex) => {
                            const hasMultipleItems =
                                formatType === 'with_item_detail' &&
                                transaction.salesOrderLineItems &&
                                Array.isArray(transaction.salesOrderLineItems) &&
                                transaction.salesOrderLineItems.length > 1;

                            const firstLineItem = transaction.salesOrderLineItems?.[0];
                            const firstLineItemHasProcessed =
                                firstLineItem?.processedList && firstLineItem.processedList.length > 0;

                            flatItems.push({
                                type: 'transaction-row',
                                data: transaction,
                                id: `trans-${transaction.id || transIndex}-${date}`,
                                companyId: companyData.companyId,
                                depth: 2,
                                hasMultipleItems: hasMultipleItems,
                                hasProcessedItems: firstLineItemHasProcessed,
                            });

                            // Processed rows for FIRST lineItem
                            if (firstLineItemHasProcessed && expandedItems[`items-${transaction.id}`]) {
                                firstLineItem.processedList.forEach((processedItem, procIndex) => {
                                    flatItems.push({
                                        type: 'processed-row',
                                        data: processedItem,
                                        parentTransaction: transaction,
                                        parentLineItem: firstLineItem,
                                        id: `processed-${transaction.id}-0-${procIndex}-${date}`,
                                        companyId: companyData.companyId,
                                        depth: 4,
                                    });
                                });
                            }

                            // Additional line items
                            if (hasMultipleItems) {
                                transaction.salesOrderLineItems.slice(1).forEach((lineItem, itemIndex) => {
                                    const lineItemHasProcessed =
                                        lineItem.processedList && lineItem.processedList.length > 0;

                                    flatItems.push({
                                        type: 'item-row',
                                        data: lineItem,
                                        parentTransaction: transaction,
                                        id: `item-${itemIndex + 1}-${transaction.id}-${date}`,
                                        companyId: companyData.companyId,
                                        depth: 3,
                                        hasProcessedItems: lineItemHasProcessed,
                                    });

                                    if (lineItemHasProcessed && expandedItems[`lineitem-${lineItem.id}`]) {
                                        lineItem.processedList.forEach((processedItem, procIndex) => {
                                            flatItems.push({
                                                type: 'processed-row',
                                                data: processedItem,
                                                parentTransaction: transaction,
                                                parentLineItem: lineItem,
                                                id: `processed-lineitem-${lineItem.id}-${procIndex}-${date}`,
                                                companyId: companyData.companyId,
                                                depth: 4,
                                            });
                                        });
                                    }
                                });
                            }
                        });
                    });
                });
            }
        } else {
            // DYNAMIC FORMAT PROCESSING
            const selectedFormat = dynamicOptions.find((opt) => opt.value === reportType);
            const formatDefName = selectedFormat?.formatData?.defName;

            if (companyFilter.length > 1) {
                const { grandTotalQty, grandTotalAmt } = calculateGrandTotals();
                flatItems.push({
                    type: 'grand-total',
                    data: { totalQty: grandTotalQty, totalAmt: grandTotalAmt },
                    id: 'grand-total',
                    depth: 0,
                });
            }

            salesReport.forEach((companyData, companyIndex) => {
                flatItems.push({
                    type: 'company-header',
                    data: companyData,
                    id: `company-${companyData.companyId || companyIndex}`,
                    companyId: companyData.companyId,
                    depth: 0,
                });

                const flattenedTransactions = companyData.data || [];

                if (formatDefName === 'PurchaseDetailParty') {
                    const groupedByParty = flattenedTransactions.reduce((acc, trans) => {
                        const party = trans.accountname || trans.partyName || trans.party_name || 'Unknown Party';
                        if (!acc[party]) acc[party] = [];
                        acc[party].push(trans);
                        return acc;
                    }, {});

                    Object.entries(groupedByParty).forEach(([partyName, partyTrans], partyIndex) => {
                        flatItems.push({
                            type: 'party-header',
                            data: { partyName, transactions: partyTrans },
                            id: `party-${partyName}-${partyIndex}`,
                            companyId: companyData.companyId,
                            depth: 1,
                        });

                        flatItems.push({
                            type: 'column-headers',
                            data: null,
                            id: `headers-${partyName}-${partyIndex}`,
                            companyId: companyData.companyId,
                            depth: 2,
                        });

                        partyTrans.forEach((transaction, transIndex) => {
                            const hasMultipleItems =
                                formatType === 'with_item_detail' &&
                                transaction.salesOrderLineItems &&
                                Array.isArray(transaction.salesOrderLineItems) &&
                                transaction.salesOrderLineItems.length > 1;

                            const firstLineItem = transaction.salesOrderLineItems?.[0];
                            const firstLineItemHasProcessed =
                                firstLineItem?.processedList && firstLineItem.processedList.length > 0;

                            flatItems.push({
                                type: 'transaction-row',
                                data: transaction,
                                id: `trans-${transaction.id || transIndex}-${partyName}`,
                                companyId: companyData.companyId,
                                depth: 2,
                                hasMultipleItems: hasMultipleItems,
                                hasProcessedItems: firstLineItemHasProcessed,
                            });

                            if (firstLineItemHasProcessed && expandedItems[`items-${transaction.id}`]) {
                                firstLineItem.processedList.forEach((processedItem, procIndex) => {
                                    flatItems.push({
                                        type: 'processed-row',
                                        data: processedItem,
                                        parentTransaction: transaction,
                                        parentLineItem: firstLineItem,
                                        id: `processed-${transaction.id}-0-${procIndex}-${partyName}`,
                                        companyId: companyData.companyId,
                                        depth: 4,
                                    });
                                });
                            }

                            if (hasMultipleItems) {
                                transaction.salesOrderLineItems.slice(1).forEach((lineItem, itemIndex) => {
                                    const lineItemHasProcessed =
                                        lineItem.processedList && lineItem.processedList.length > 0;

                                    flatItems.push({
                                        type: 'item-row',
                                        data: lineItem,
                                        parentTransaction: transaction,
                                        id: `item-${itemIndex + 1}-${transaction.id}-${partyName}`,
                                        companyId: companyData.companyId,
                                        depth: 3,
                                        hasProcessedItems: lineItemHasProcessed,
                                    });

                                    if (lineItemHasProcessed && expandedItems[`lineitem-${lineItem.id}`]) {
                                        lineItem.processedList.forEach((processedItem, procIndex) => {
                                            flatItems.push({
                                                type: 'processed-row',
                                                data: processedItem,
                                                parentTransaction: transaction,
                                                parentLineItem: lineItem,
                                                id: `processed-lineitem-${lineItem.id}-${procIndex}-${partyName}`,
                                                companyId: companyData.companyId,
                                                depth: 4,
                                            });
                                        });
                                    }
                                });
                            }
                        });
                    });

                } else if (formatDefName === 'PurchaseDetailDate') {
                    const groupedByDate = flattenedTransactions.reduce((acc, trans) => {
                        const date = trans.transactionAt || 'No Date';
                        if (!acc[date]) acc[date] = [];
                        acc[date].push(trans);
                        return acc;
                    }, {});

                    flatItems.push({
                        type: 'column-headers',
                        data: null,
                        id: `headers-company-${companyData.companyId}`,
                        companyId: companyData.companyId,
                        depth: 1,
                    });

                    Object.entries(groupedByDate).forEach(([date, dateTrans], dateIndex) => {
                        flatItems.push({
                            type: 'date-header',
                            data: { date, transactions: dateTrans },
                            id: `date-${date}-${dateIndex}`,
                            companyId: companyData.companyId,
                            depth: 2,
                        });

                        dateTrans.forEach((transaction, transIndex) => {
                            const hasMultipleItems =
                                formatType === 'with_item_detail' &&
                                transaction.salesOrderLineItems &&
                                Array.isArray(transaction.salesOrderLineItems) &&
                                transaction.salesOrderLineItems.length > 1;

                            const firstLineItem = transaction.salesOrderLineItems?.[0];
                            const firstLineItemHasProcessed =
                                firstLineItem?.processedList && firstLineItem.processedList.length > 0;

                            flatItems.push({
                                type: 'transaction-row',
                                data: transaction,
                                id: `trans-${transaction.id || transIndex}-${date}`,
                                companyId: companyData.companyId,
                                depth: 3,
                                hasMultipleItems: hasMultipleItems,
                                hasProcessedItems: firstLineItemHasProcessed,
                            });

                            if (firstLineItemHasProcessed && expandedItems[`items-${transaction.id}`]) {
                                firstLineItem.processedList.forEach((processedItem, procIndex) => {
                                    flatItems.push({
                                        type: 'processed-row',
                                        data: processedItem,
                                        parentTransaction: transaction,
                                        parentLineItem: firstLineItem,
                                        id: `processed-${transaction.id}-0-${procIndex}-${date}`,
                                        companyId: companyData.companyId,
                                        depth: 4,
                                    });
                                });
                            }

                            if (hasMultipleItems) {
                                transaction.salesOrderLineItems.slice(1).forEach((lineItem, itemIndex) => {
                                    const lineItemHasProcessed =
                                        lineItem.processedList && lineItem.processedList.length > 0;

                                    flatItems.push({
                                        type: 'item-row',
                                        data: lineItem,
                                        parentTransaction: transaction,
                                        id: `item-${itemIndex + 1}-${transaction.id}-${date}`,
                                        companyId: companyData.companyId,
                                        depth: 4,
                                        hasProcessedItems: lineItemHasProcessed,
                                    });

                                    if (lineItemHasProcessed && expandedItems[`lineitem-${lineItem.id}`]) {
                                        lineItem.processedList.forEach((processedItem, procIndex) => {
                                            flatItems.push({
                                                type: 'processed-row',
                                                data: processedItem,
                                                parentTransaction: transaction,
                                                parentLineItem: lineItem,
                                                id: `processed-lineitem-${lineItem.id}-${procIndex}-${date}`,
                                                companyId: companyData.companyId,
                                                depth: 5,
                                            });
                                        });
                                    }
                                });
                            }
                        });
                    });

                } else {
                    // Default dynamic format
                    flatItems.push({
                        type: 'column-headers',
                        data: null,
                        id: `headers-company-${companyData.companyId}`,
                        companyId: companyData.companyId,
                        depth: 1,
                    });

                    flattenedTransactions.forEach((transaction, transIndex) => {
                        const hasMultipleItems =
                            formatType === 'with_item_detail' &&
                            transaction.salesOrderLineItems &&
                            Array.isArray(transaction.salesOrderLineItems) &&
                            transaction.salesOrderLineItems.length > 1;

                        const firstLineItem = transaction.salesOrderLineItems?.[0];
                        const firstLineItemHasProcessed =
                            firstLineItem?.processedList && firstLineItem.processedList.length > 0;

                        flatItems.push({
                            type: 'transaction-row',
                            data: transaction,
                            id: `trans-${transaction.id || transIndex}`,
                            companyId: companyData.companyId,
                            depth: 2,
                            hasMultipleItems: hasMultipleItems,
                            hasProcessedItems: firstLineItemHasProcessed,
                        });

                        if (firstLineItemHasProcessed && expandedItems[`items-${transaction.id}`]) {
                            firstLineItem.processedList.forEach((processedItem, procIndex) => {
                                flatItems.push({
                                    type: 'processed-row',
                                    data: processedItem,
                                    parentTransaction: transaction,
                                    parentLineItem: firstLineItem,
                                    id: `processed-${transaction.id}-0-${procIndex}`,
                                    companyId: companyData.companyId,
                                    depth: 4,
                                });
                            });
                        }

                        if (hasMultipleItems) {
                            transaction.salesOrderLineItems.slice(1).forEach((lineItem, itemIndex) => {
                                const lineItemHasProcessed =
                                    lineItem.processedList && lineItem.processedList.length > 0;

                                flatItems.push({
                                    type: 'item-row',
                                    data: lineItem,
                                    parentTransaction: transaction,
                                    id: `item-${itemIndex + 1}-${transaction.id}`,
                                    companyId: companyData.companyId,
                                    depth: 3,
                                    hasProcessedItems: lineItemHasProcessed,
                                });

                                if (lineItemHasProcessed && expandedItems[`lineitem-${lineItem.id}`]) {
                                    lineItem.processedList.forEach((processedItem, procIndex) => {
                                        flatItems.push({
                                            type: 'processed-row',
                                            data: processedItem,
                                            parentTransaction: transaction,
                                            parentLineItem: lineItem,
                                            id: `processed-lineitem-${lineItem.id}-${procIndex}`,
                                            companyId: companyData.companyId,
                                            depth: 4,
                                        });
                                    });
                                }
                            });
                        }
                    });
                }
            });
        }

        return flatItems;
    }, [salesReport, reportType, formatType, expandedItems, isStaticReportType, dynamicOptions]);

    useEffect(() => {
        if (salesReport.length > 0) {
            const flat = flattenHierarchicalData();
            setFlattenedData(flat);

            if (listRef.current) {
                listRef.current.resetAfterIndex(0);
            }
        }
    }, [salesReport, flattenHierarchicalData]);

    const getItemSize = useCallback(
        (index) => {
            const item = flattenedData[index];
            if (!item) return 40;

            switch (item.type) {
                case 'company-header':
                    return 40;
                case 'account-header':
                case 'party-header':
                case 'date-header':
                    return 30;
                case 'column-headers':
                    return 35;
                case 'transaction-row':
                    return 40;
                case 'item-row':
                    return 40;
                case 'grand-total':
                    return 45;
                default:
                    return 40;
            }
        },
        [flattenedData],
    );

    // Add this function near your other handlers
    const handleTransactionClick = useCallback(
        async (transaction) => {
            if (transaction.companyId === user?.loginhistory?.companyId) {
                await dispatch(getSaleOrderById(transaction.id));
                setIsOpenPurchaseReturnSingleView(true);
            }
        },
        [dispatch, user?.loginhistory?.companyId],
    );

    const formatCurrencyWithSmallDecimals = useCallback((value) => {
        if (value == null || value === '') return '₹0';
        const numValue = Number(value);
        if (isNaN(numValue)) return '₹0';

        const formatted = numValue.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        const parts = formatted.toString().split('.');

        if (parts.length === 2) {
            return (
                <>
                    ₹{parts[0]}.<span style={{ fontSize: '0.7em' }}>{parts[1]}</span>
                </>
            );
        }

        return `₹${formatted}`;
    }, []);

    // Add this helper function before RowRenderer
    const formatDateForDisplay = useCallback((dateValue) => {
        if (!dateValue) return '-';

        // If already formatted as DD-MM-YYYY
        if (typeof dateValue === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
            return dateValue;
        }

        // Try parsing with moment
        const m = moment(dateValue);
        if (m.isValid()) {
            return m.format('DD-MM-YYYY');
        }

        // If it's a timestamp
        if (!isNaN(dateValue)) {
            const timestamp = Number(dateValue);
            const date = timestamp > 9999999999 ? moment(timestamp) : moment(timestamp * 1000);
            if (date.isValid()) {
                return date.format('DD-MM-YYYY');
            }
        }

        return '-';
    }, []);

    const COL_FLEX = {
        transactionAt: 1,
        docno: 0.8,
        accountname: 1.8,
        mobile: 1.2,
        itemName: 2,
        itemRate: 0.8,
        daybookname: 1.5,
        salesmanname: 1.3,
        notes: 1.3,
        qty1: 0.8,
        qty2: 0.8,
        qty3: 0.8,
        deliveredQty1: 0.8,
        deliveredQty2: 0.8,
        deliveredQty3: 0.8,
        pendingQty1: 0.8,
        pendingQty2: 0.8,
        pendingQty3: 0.8,
        netAmount: 1.2,
        billAmount: 1.2,
        pendingAmt: 1.2,
        statusLabel: 1,
    };
    const DEFAULT_COL_FLEX = 1;
    const buildTemplateColumns = useCallback(() => {
        const visibleColumns = dynamicColumns.filter(
            col => columnVisibility[col.id] !== false
        );
        const frWidths = visibleColumns
            .map(col => `${COL_FLEX[col.id] || DEFAULT_COL_FLEX}fr`)
            .join(' ');
        return `35px ${frWidths}`; // 35px sirf toggle ke liye fixed
    }, [dynamicColumns, columnVisibility]);

    const RowRenderer = useCallback(
        ({ index, style }) => {
            const item = flattenedData[index];

            if (!item) return <div style={style} />;

            const expandedItems = expandedItemsRef.current;

            const COLS = "40px 80px 80px 120px 80px 130px 70px 110px 90px 90px 1fr 90px 90px 130px 110px 110px";
            const renderContent = () => {
                switch (item.type) {

                    case 'grand-total':
                        const grandTotalData = item.data;
                        return (
                            <Box
                                style={style}
                                bg={isDark ? '#1a3a6b' : '#E8E2D9'}
                                p="10px"
                                borderTop="3px solid"
                                borderBottom="2px solid"
                                borderColor={borderColor}
                                fontWeight="bold"
                                position="sticky"
                                bottom="0"
                                zIndex="3">
                                <Flex justifyContent="space-between" alignItems="center">
                                    <Text
                                        fontWeight="bold"
                                        fontSize="16px"
                                        color={`${isDark ? '#fff' : '#000'}`}>
                                        GRAND TOTAL ({companyFilter.length} Companies)
                                    </Text>
                                    <Flex gap={6}>
                                        <Text fontWeight="600" fontSize="15px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Total Qty: {grandTotalData.totalQty.toLocaleString()}
                                        </Text>
                                        <Text fontWeight="bold" fontSize="15px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Total Amt: ₹{grandTotalData.totalAmt.toLocaleString()}
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Box>
                        );

                    case 'company-header': {
                        const company = item.data;
                        const companyTotalQty =
                            company.data?.reduce((sum, account) => sum + (account.totalQty1 || 0), 0) || 0;
                        const companyTotalAmt =
                            company.data?.reduce((sum, account) => sum + (account.totalNetAmt || 0), 0) || 0;

                        return (
                            <Box
                                style={style}
                                bg="#F1F0E8"
                                _dark={{ bg: 'blue.700' }}
                                p="8px"
                                borderBottom="2px solid"
                                borderColor={borderColor}
                                fontWeight="bold">
                                <Flex justifyContent="space-between" alignItems="center">
                                    <Text
                                        fontWeight="bold"
                                        fontSize="16px"
                                        color={`${isDark ? '#fff' : '#000'}`}
                                        title={company.companyName}>
                                        {company.companyName}
                                    </Text>
                                    <Flex gap={6}>
                                        <Text fontWeight="600" fontSize="14px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Qty: {companyTotalQty.toLocaleString()}
                                        </Text>
                                        <Text fontWeight="bold" fontSize="14px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Amt: ₹{companyTotalAmt.toLocaleString()}
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Box>
                        );
                    }

                    case 'account-header': {
                        const account = item.data;
                        return (
                            <Box
                                style={style}
                                bg="#F5EEE6"
                                _dark={{ bg: 'blue.900' }}
                                p="6px"
                                pl="20px"
                                borderBottom="1px solid"
                                borderColor={borderColor}
                                cursor="pointer"
                                onClick={() => toggleExpand(`account-${item.accountId}`)}>
                                <Flex justifyContent="space-between" alignItems="center">
                                    <Flex align="center" gap={2}>
                                        <Text
                                            fontWeight="600"
                                            fontSize="14px"
                                            color={`${isDark ? '#fff' : '#000'}`}
                                            title={account.accountName}>
                                            {account.accountName}
                                        </Text>
                                        {expandedItems[`account-${item.accountId}`] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    </Flex>
                                    <Flex gap={6}>
                                        <Text fontWeight="600" fontSize="13px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Qty: {account.totalQty1?.toLocaleString() || 0}
                                        </Text>
                                        <Text fontWeight="600" fontSize="13px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Amt: ₹{account.totalNetAmt?.toLocaleString() || 0}
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Box>
                        );
                    }

                    case 'party-header': {
                        const partyData = item.data;
                        const partyQty = partyData.transactions?.reduce((sum, t) => sum + (t?.totalQty1 || 0), 0) || 0;
                        const partyAmt = partyData.transactions?.reduce((sum, t) => sum + (t?.netAmount || 0), 0) || 0;

                        return (
                            <Box
                                style={style}
                                bg="#F5EEE6"
                                _dark={{ bg: 'blue.900' }}
                                p="6px"
                                pl="20px"
                                borderBottom="1px solid"
                                borderColor={borderColor}>
                                <Flex justifyContent="space-between" alignItems="center">
                                    <Text
                                        fontWeight="600"
                                        fontSize="14px"
                                        color={`${isDark ? '#fff' : '#000'}`}
                                        title={partyData.partyName}>
                                        {partyData.partyName}
                                    </Text>
                                    <Flex gap={6}>
                                        <Text fontWeight="600" fontSize="13px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Qty: {partyQty.toLocaleString()}
                                        </Text>
                                        <Text fontWeight="600" fontSize="13px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Amt: ₹{partyAmt.toLocaleString()}
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Box>
                        );
                    }

                    case 'date-header': {
                        const dateData = item.data;
                        const dateQty = dateData.transactions?.reduce((sum, t) => sum + (t?.totalQty1 || 0), 0) || 0;
                        const dateAmt = dateData.transactions?.reduce((sum, t) => sum + (t?.netAmount || 0), 0) || 0;

                        return (
                            <Box
                                style={style}
                                bg="gray.100"
                                _dark={{ bg: 'gray.700' }}
                                p="6px"
                                borderBottom="1px solid"
                                borderColor={borderColor}>
                                <Flex justifyContent="space-between" alignItems="center">
                                    <Text fontWeight="600" fontSize="13px" color={`${isDark ? '#fff' : '#000'}`}>
                                        {formatDateForDisplay(dateData.date)}
                                    </Text>
                                    <Flex gap={6}>
                                        <Text fontSize="13px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Qty: {dateQty.toLocaleString()}
                                        </Text>
                                        <Text fontSize="13px" color={`${isDark ? '#fff' : '#000'}`}>
                                            Amt: ₹{dateAmt.toLocaleString()}
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Box>
                        );
                    }

                    case 'column-headers': {
                        // Get visible columns from dynamicColumns
                        const visibleColumns = dynamicColumns.filter(col => columnVisibility[col.id] !== false);

                        return (
                            <Grid
                                style={style}
                                templateColumns={buildTemplateColumns()}
                                bg={bg}
                                borderY="1px solid"
                                borderColor={borderColor}
                                position="sticky"
                                top="0"
                                zIndex="2"
                                fontWeight="700"
                                fontSize="10px"
                                letterSpacing="0.5px"
                                px={2}
                                alignItems="center">

                                {/* Always show expand/collapse column */}
                                <GridItem p="8px" />

                                {/* Dynamically render headers based on visible columns */}
                                {visibleColumns.map((col) => {
                                    // Determine color based on column type
                                    let headerColor = isDark ? 'whiteAlpha.600' : 'gray.800';
                                    const id = col.id; // exact match on column id

                                    if (['qty1', 'qty2', 'qty3'].includes(id)) {
                                        headerColor = isDark ? '#60a5fa' : '#2563eb'; // 🔵 Blue - ORD
                                    } else if (['deliveredQty1', 'deliveredQty2', 'deliveredQty3'].includes(id)) {
                                        headerColor = isDark ? '#4ade80' : '#16a34a'; // 🟢 Green - DEL
                                    } else if (['pendingQty1', 'pendingQty2', 'pendingQty3'].includes(id)) {
                                        headerColor = '#ef4444'; // 🔴 Red - PND
                                    } else {
                                        headerColor = isDark ? 'whiteAlpha.600' : 'gray.800'; // default
                                    }

                                    // Apply alignment
                                    const isNumeric = col.isNumeric || col.id.includes('Amount') || col.id.includes('amt');

                                    return (
                                        <GridItem
                                            key={col.id}
                                            p="8px"
                                            textAlign={isNumeric ? 'right' : 'left'}
                                            color={headerColor}
                                            title={col.header}>
                                            {col.header.toUpperCase()}
                                        </GridItem>
                                    );
                                })}
                            </Grid>
                        );
                    }

                    case 'transaction-row': {
                        const transaction = item.data;
                        const isOddRow = index % 2 !== 0;
                        const lineItems = transaction.salesOrderLineItems || [];
                        const isExpanded = !!expandedItems[`items-${transaction.id}`];
                        const hasChallans = item.hasProcessedItems === true;

                        const stats = lineItems.map((li) => getDeliveryStats(li));
                        const aggOrdered = stats.reduce((s, st) => s + st.ordered, 0);
                        const aggPending = stats.reduce((s, st) => s + st.pending, 0);
                        const hasAnyQty = aggOrdered > 0 || aggPending > 0;

                        const displayNetAmt = Number(transaction.netAmount || transaction.basicAmount || 0);
                        const displayPendAmt = Number(transaction.pendingAmt || 0);

                        let statusLabel, statusBg, statusColor;
                        if (!hasAnyQty || aggPct === 0) {
                            statusLabel = 'Pending'; statusBg = 'rgba(239,68,68,0.12)'; statusColor = '#ef4444';
                        } else if (aggPct >= 100) {
                            statusLabel = 'Delivered'; statusBg = 'rgba(34,197,94,0.12)'; statusColor = '#22c55e';
                        } else {
                            statusLabel = 'Partial'; statusBg = 'rgba(251,146,60,0.12)'; statusColor = '#fb923c';
                        }

                        const rowBg = isOddRow
                            ? isDark ? 'rgba(2,14,56,0.7)' : 'rgba(0,63,140,0.04)'
                            : isDark ? 'rgba(16,31,74,0.5)' : 'white';

                        const hoverBg = isDark ? 'rgba(0,63,140,0.2)' : 'rgba(0,63,140,0.07)';

                        const aggOrdered1 = stats.reduce((s, st) => s + st.ordered1, 0);
                        const aggDelivered1 = stats.reduce((s, st) => s + st.delivered1, 0);
                        // For status badge - use qty1 as primary
                        const aggPct = aggOrdered1 > 0
                            ? Math.min(100, Math.round((aggDelivered1 / aggOrdered1) * 100))
                            : 0;

                        return (
                            <Box
                                style={style}
                                borderBottom="1px solid"
                                borderColor={borderColor}>
                                <Grid
                                    className="trans-row"
                                    templateColumns={buildTemplateColumns()}
                                    alignItems="center"
                                    h="100%"
                                    style={{ '--hover-bg': hoverBg, background: rowBg }}
                                    px={2}>

                                    {/* Col 0: Expand Toggle */}
                                    <GridItem display="flex" alignItems="center" justifyContent="center">
                                        {hasChallans ? (
                                            <Box
                                                as="button"
                                                w="20px" h="20px" borderRadius="4px"
                                                border="none"
                                                background="transparent"
                                                cursor="pointer"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                    toggleExpand(`items-${transaction.id}`);
                                                }}>
                                                <Text
                                                    fontSize="9px" fontWeight="bold"
                                                    color={isDark ? 'blue.300' : 'blue.600'}
                                                    lineHeight="1"
                                                    style={{
                                                        display: 'inline-block',
                                                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s',
                                                    }}>
                                                    ▶
                                                </Text>
                                            </Box>
                                        ) : (
                                            <Box w="20px" h="20px" />
                                        )}
                                    </GridItem>

                                    {/* Col 1: Date */}
                                    <GridItem cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        <Text fontSize="12px" color={isDark ? 'whiteAlpha.700' : 'gray.800'}>
                                            {formatDateForDisplay(transaction.transactionAt)}
                                        </Text>
                                    </GridItem>

                                    {/* Col 2: Doc # */}
                                    <GridItem cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        <Box display="inline-flex" alignItems="center" justifyContent="center"
                                            minW="28px" h="22px" px="5px" borderRadius="4px">
                                            <Text fontSize="12px" fontWeight="700" color={isDark ? 'blue.300' : 'blue.700'}>
                                                {transaction.docno || '-'}
                                            </Text>
                                        </Box>
                                    </GridItem>

                                    {/* Col 3: Party */}
                                    <GridItem overflow="hidden" cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        <Text fontSize="12px" fontWeight="600"
                                            color={isDark ? 'white' : 'gray.800'}
                                            noOfLines={1} title={transaction.accountname}>
                                            {transaction.accountname || '-'}
                                        </Text>
                                    </GridItem>

                                    {/* Col 4: Mobile */}
                                    <GridItem cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        <Text fontSize="12px" color={isDark ? 'whiteAlpha.600' : 'gray.800'} noOfLines={1}>
                                            {transaction.mobile || '—'}
                                        </Text>
                                    </GridItem>

                                    {/* Col 5: Item Name */}
                                    <GridItem overflow="hidden" cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        {lineItems.length > 0 ? (
                                            <Text fontSize="12px" fontWeight="500"
                                                color={isDark ? 'whiteAlpha.900' : 'gray.800'}
                                                noOfLines={1} title={lineItems[0].itemName}>
                                                {lineItems[0].itemName || '-'}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.800'}>—</Text>
                                        )}
                                    </GridItem>

                                    {/* Col 6: Rate */}
                                    <GridItem textAlign="right" mr={2} cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        {lineItems.length > 0 ? (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.700' : 'gray.800'}>
                                                {Number(lineItems[0].itemRate || 0) > 0
                                                    ? `₹${Number(lineItems[0].itemRate).toLocaleString()}`
                                                    : '—'}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.800'}>—</Text>
                                        )}
                                    </GridItem>

                                    {/* Col 7: Daybook */}
                                    <GridItem overflow="hidden" cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        <Text fontSize="12px" color={isDark ? 'whiteAlpha.600' : 'gray.800'}
                                            noOfLines={1} title={transaction.daybookname}>
                                            {transaction.daybookname || '—'}
                                        </Text>
                                    </GridItem>

                                    {/* Col 8: Salesman */}
                                    <GridItem overflow="hidden" cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        <Text fontSize="12px" color={isDark ? 'whiteAlpha.600' : 'gray.800'}
                                            noOfLines={1} title={transaction.salesmanname}>
                                            {transaction.salesmanname || '—'}
                                        </Text>
                                    </GridItem>

                                    {/* Col 9: Notes */}
                                    <GridItem overflow="hidden" cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        <Text fontSize="12px" color={isDark ? 'whiteAlpha.500' : 'gray.800'}
                                            noOfLines={1} title={transaction.notes}>
                                            {transaction.notes || '—'}
                                        </Text>
                                    </GridItem>

                                    {/* ORDQTY1 - Blue */}
                                    <GridItem textAlign={'center'}>
                                        {lineItems.length > 0 ? (
                                            <Text fontSize="12px" fontWeight="600" color={isDark ? '#60a5fa' : '#2563eb'}>
                                                {(lineItems[0].qty1 || 0) > 0 ? `${(lineItems[0].qty1).toLocaleString()}` : '—'}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.300'}>—</Text>
                                        )}
                                    </GridItem>
                                    <GridItem textAlign={'center'}>
                                        {lineItems.length > 0 ? (
                                            <Text fontSize="12px" fontWeight="600" color={isDark ? '#60a5fa' : '#2563eb'}>
                                                {(lineItems[0].qty2 || 0) > 0 ? `${(lineItems[0].qty2).toLocaleString()}` : '—'}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.300'}>—</Text>
                                        )}
                                    </GridItem>
                                    <GridItem textAlign={'center'}>
                                        {lineItems.length > 0 ? (
                                            <Text fontSize="12px" fontWeight="600" color={isDark ? '#60a5fa' : '#2563eb'}>
                                                {(lineItems[0].qty3 || 0) > 0 ? `${(lineItems[0].qty3).toLocaleString()}` : '—'}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.300'}>—</Text>
                                        )}
                                    </GridItem>

                                    {/* DELQTY1/2/3 - Green (these are the 3 empty GridItems - leave blank, colors show in item-row) */}
                                    <GridItem />
                                    <GridItem />
                                    <GridItem />

                                    {/* PNDQTY1 - Red */}
                                    <GridItem textAlign={'center'}>
                                        {lineItems.length > 0 ? (
                                            <Text fontSize="12px" fontWeight="600" color="#ef4444">
                                                {(lineItems[0].pendingQty1 || 0) > 0 ? `${(lineItems[0].pendingQty1).toLocaleString()}` : '—'}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.800'}>—</Text>
                                        )}
                                    </GridItem>
                                    <GridItem textAlign={'center'}>
                                        {lineItems.length > 0 ? (
                                            <Text fontSize="12px" fontWeight="600" color="#ef4444">
                                                {(lineItems[0].pendingQty2 || 0) > 0 ? `${(lineItems[0].pendingQty2).toLocaleString()}` : '—'}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.800'}>—</Text>
                                        )}
                                    </GridItem>
                                    <GridItem textAlign={'center'}>
                                        {lineItems.length > 0 ? (
                                            <Text fontSize="12px" fontWeight="600" color="#ef4444">
                                                {(lineItems[0].pendingQty3 || 0) > 0 ? `${(lineItems[0].pendingQty3).toLocaleString()}` : '—'}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.800'}>—</Text>
                                        )}
                                    </GridItem>

                                    {/* Col: NET AMT */}
                                    <GridItem textAlign="right" cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        <Text fontSize="12px" fontWeight="600"
                                            color={displayNetAmt > 0
                                                ? isDark ? 'whiteAlpha.800' : 'gray.700'
                                                : isDark ? 'whiteAlpha.300' : 'gray.300'}
                                            fontFamily="mono">
                                            ₹{displayNetAmt.toLocaleString()}
                                        </Text>
                                    </GridItem>

                                    {/* Col: PENDING AMT */}
                                    <GridItem textAlign="right" cursor="pointer" onPointerDown={() => handleTransactionClick(transaction)}>
                                        {displayPendAmt > 0 ? (
                                            <Text fontSize="12px" fontWeight="600" fontFamily="mono">
                                                ₹{displayPendAmt.toLocaleString()}
                                            </Text>
                                        ) : (
                                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.300' : 'gray.300'}>—</Text>
                                        )}
                                    </GridItem>

                                </Grid>
                            </Box>
                        );
                    }

                    case 'item-row': {
                        const lineItem = item.data;
                        const parentTrans = item.parentTransaction;
                        const {
                            ordered1, pending1,
                            ordered2, pending2,
                            ordered3, pending3,
                        } = getDeliveryStats(lineItem);

                        const hasLineItemChallans = item.hasProcessedItems === true;
                        const isLineExpanded = !!expandedItems[`lineitem-${lineItem.id}`];

                        return (
                            <Grid
                                style={style}
                                templateColumns={buildTemplateColumns()}
                                alignItems="center"
                                minH="40px"
                                bg={isDark ? 'rgba(30,58,138,0.15)' : 'rgba(219,234,254,0.4)'}
                                borderLeft="3px solid"
                                borderLeftColor="blue.400"
                                borderBottom="1px dashed"
                                borderColor={borderColor}
                                px={2}>

                                {/* Col 0: Expand icon */}
                                <GridItem display="flex" alignItems="center" justifyContent="center">
                                    {hasLineItemChallans ? (
                                        <Box w="20px" h="20px" borderRadius="4px" cursor="pointer"
                                            onPointerDown={(e) => {
                                                e.stopPropagation();
                                                toggleExpand(`lineitem-${lineItem.id}`);
                                            }}>
                                            <Text fontSize="9px" fontWeight="bold"
                                                color={isDark ? 'orange.300' : 'orange.500'}
                                                style={{
                                                    transform: isLineExpanded ? 'rotate(90deg)' : 'none',
                                                    transition: 'transform 0.2s',
                                                    display: 'block',
                                                }}>▶</Text>
                                        </Box>
                                    ) : <Box w="20px" h="20px" />}
                                </GridItem>

                                {/* Col 1-4: empty (date, doc, party, mobile) */}
                                <GridItem /><GridItem /><GridItem /><GridItem />

                                {/* Col 5: Item Name */}
                                <GridItem overflow="hidden" cursor="pointer" onClick={() => handleTransactionClick(parentTrans)}>
                                    <Text fontSize="12px" fontWeight="500"
                                        color={isDark ? 'whiteAlpha.800' : 'gray.700'}
                                        noOfLines={1} title={lineItem.itemName}>
                                        {lineItem.itemName}
                                    </Text>
                                </GridItem>

                                {/* Col 6: Rate */}
                                <GridItem textAlign="right">
                                    <Text fontSize="11px" color={isDark ? 'whiteAlpha.700' : 'gray.600'}>
                                        {Number(lineItem.itemRate || 0) > 0
                                            ? `₹${Number(lineItem.itemRate).toLocaleString()}`
                                            : '—'}
                                    </Text>
                                </GridItem>

                                {/* Col 7, 8, 9: empty (daybook, salesman, notes) */}
                                <GridItem /><GridItem /><GridItem />

                                <GridItem textAlign={'center'}>
                                    <Text fontSize="13px" fontWeight="600" color={isDark ? '#60a5fa' : '#2563eb'}>
                                        {ordered1 > 0 ? ordered1.toLocaleString() : '—'}
                                    </Text>
                                </GridItem>
                                <GridItem textAlign={'center'}>
                                    <Text fontSize="13px" fontWeight="600" color={isDark ? '#60a5fa' : '#2563eb'}>
                                        {ordered2 > 0 ? ordered2.toLocaleString() : '—'}
                                    </Text>
                                </GridItem>
                                <GridItem textAlign={'center'}>
                                    <Text fontSize="13px" fontWeight="600" color={isDark ? '#60a5fa' : '#2563eb'}>
                                        {ordered3 > 0 ? ordered3.toLocaleString() : '—'}
                                    </Text>
                                </GridItem>

                                {/* ✅ deliveredQty1/2/3 — item-row mein bhi BLANK, challan row mein dikhega */}
                                <GridItem />
                                <GridItem />
                                <GridItem />

                                {/* pendingQty1/2/3 — Orange */}
                                <GridItem textAlign={'center'}>
                                    <Text fontSize="13px" fontWeight="600" color="#f97316">
                                        {pending1 > 0 ? pending1.toLocaleString() : '—'}
                                    </Text>
                                </GridItem>
                                <GridItem textAlign={'center'}>
                                    <Text fontSize="13px" fontWeight="600" color="#f97316">
                                        {pending2 > 0 ? pending2.toLocaleString() : '—'}
                                    </Text>
                                </GridItem>
                                <GridItem textAlign={'center'}>
                                    <Text fontSize="13px" fontWeight="600" color="#f97316">
                                        {pending3 > 0 ? pending3.toLocaleString() : '—'}
                                    </Text>
                                </GridItem>

                                {/* NET AMT: empty for item-row */}
                                <GridItem />

                                {/* PENDING AMT: empty for item-row */}
                                <GridItem />
                            </Grid>
                        );
                    }

                    case 'processed-row': {
                        const processedData = item.data;
                        const parentLineItem = item.parentLineItem;

                        const challanQty1 = Number(processedData.qty1 || 0);
                        const challanQty2 = Number(processedData.qty2 || 0);
                        const challanQty3 = Number(processedData.qty3 || 0);

                        return (
                            <Grid
                                style={style}
                                templateColumns={buildTemplateColumns()}
                                alignItems="center"
                                minH="35px"
                                bg={isDark ? 'rgba(120,53,15,0.12)' : 'rgba(255,237,213,0.5)'}
                                borderLeft="3px solid"
                                borderLeftColor="orange.400"
                                borderBottom="1px dashed"
                                borderColor={borderColor}
                                px={2}
                                fontSize="12px">

                                {/* Col 0: icon */}
                                <GridItem />

                                {/* Col 1: Challan Date */}
                                <GridItem>
                                    <Text fontSize="12px" color={isDark ? 'whiteAlpha.500' : 'gray.700'}>
                                        {processedData.transactionAt}
                                    </Text>
                                </GridItem>

                                {/* Col 2: Challan Doc # */}
                                <GridItem>
                                    <Box display="inline-flex" px="5px" py="1px" borderRadius="3px"
                                        bg={isDark ? 'rgba(251,146,60,0.2)' : 'rgba(234,88,12,0.10)'}
                                        border="1px solid" borderColor={isDark ? 'orange.600' : 'orange.300'}>
                                        <Text fontSize="12px" fontWeight="700" color={isDark ? 'orange.300' : 'orange.700'}>
                                            {processedData.docNo}
                                        </Text>
                                    </Box>
                                </GridItem>

                                {/* Col 3: Company name */}
                                <GridItem overflow="hidden">
                                    <Text fontSize="12px" color={isDark ? 'whiteAlpha.600' : 'gray.700'} noOfLines={1} title={processedData.accountName}>
                                        {processedData.accountName}
                                    </Text>
                                </GridItem>

                                {/* Col 4: empty (mobile) */}
                                <GridItem overflow="hidden">
                                    <Text fontSize="12px" color={isDark ? 'whiteAlpha.600' : 'gray.700'} noOfLines={1} title={`Type : ( ${processedData.type} )`}>
                                        Type : ( {processedData.type} )
                                    </Text>
                                </GridItem>

                                {/* Col 5: Item name */}
                                <GridItem overflow="hidden">
                                    {parentLineItem?.itemName && (
                                        <Text fontSize="12px" color={isDark ? 'blue.300' : 'blue.500'}
                                            fontStyle="italic" noOfLines={1} title={parentLineItem.itemName}>
                                            {parentLineItem.itemName}
                                        </Text>
                                    )}
                                </GridItem>

                                {/* Col 6: empty (rate) */}
                                <GridItem />

                                {/* Col 7: Daybook */}
                                <GridItem overflow="hidden">
                                    <Text fontSize="12px" color={isDark ? 'whiteAlpha.500' : 'gray.700'} noOfLines={1} title={processedData.daybookName}>
                                        {processedData.daybookName}
                                    </Text>
                                </GridItem>

                                {/* Col 8, 9: empty (salesman, notes) */}
                                <GridItem /><GridItem />

                                {/* ✅ qty1/2/3 — BLANK (ye order qty hai, challan row mein nahi dikhana) */}
                                <GridItem />
                                <GridItem />
                                <GridItem />

                                {/* ✅ deliveredQty1/2/3 — YE DIKHAO! Challan ki actual qty */}
                                <GridItem textAlign={'center'}>
                                    {challanQty1 > 0
                                        ? <Text fontWeight="700" color="#22c55e" fontSize="13px" fontFamily="mono">
                                            {challanQty1.toLocaleString()}
                                        </Text>
                                        : <Text fontSize="12px" color="#22c55e">—</Text>}
                                </GridItem>
                                <GridItem textAlign={'center'}>
                                    {challanQty2 > 0
                                        ? <Text fontWeight="700" color="#22c55e" fontSize="13px" fontFamily="mono">
                                            {challanQty2.toLocaleString()}
                                        </Text>
                                        : <Text fontSize="12px" color="#22c55e">—</Text>}
                                </GridItem>
                                <GridItem textAlign={'center'}>
                                    {challanQty3 > 0
                                        ? <Text fontWeight="700" color="#22c55e" fontSize="13px" fontFamily="mono">
                                            {challanQty3.toLocaleString()}
                                        </Text>
                                        : <Text fontSize="12px" color="#22c55e">—</Text>}
                                </GridItem>

                                {/* ✅ pendingQty1/2/3 — BLANK (challan row mein pending nahi hota) */}
                                <GridItem />
                                <GridItem />
                                <GridItem />

                                {/* NET AMT, PENDING AMT: empty */}
                                <GridItem />
                                <GridItem />
                            </Grid>
                        );
                    }
                    default:
                        return <div style={style} />;
                }
            };

            return renderContent();
        },
        [
            flattenedData,
            isDark,
            borderColor,
            bg,
            table,
            dynamicColumns,
            formatType,
            toggleExpand,
            handleTransactionClick,
            formatCurrencyWithSmallDecimals,
            textColor,
            formatDateForDisplay,
            buildTemplateColumns,
            getDeliveryStats,
        ],
    );

    useEffect(() => {
        if (salesReport.length > 0) {
            const flat = flattenHierarchicalData();
            setFlattenedData(flat);

            // Reset list to recalculate heights
            if (listRef.current) {
                listRef.current.resetAfterIndex(0);
            }
        }
    }, [salesReport, flattenHierarchicalData, calculateGrandTotals]);

    const handleShowHideFormOpen = async () => {
        setIsShowHideColumn(true);
        const formatData = reportformat.find((f) => f.title === reportType);
        if (formatData) {
            setIsEditShowHide(true);
            setEditData(formatData);
        } else {
            setIsEditShowHide(false);
            setEditData({});
        }
    };

    return (
        <>
            <Flex pr="25px" mb="8px" justifyContent="space-between" align="center" mt="50px" overflow="hidden">
                <Flex>
                    <Image
                        src={back}
                        alt="Go Back"
                        width="15px"
                        onClick={() => window.history.back()}
                        style={{ cursor: 'pointer' }}
                    />
                    <Text color={textColor} fontSize="15px" fontWeight="700" lineHeight="100%" ml={5}>
                        Sale Order Detail
                    </Text>
                </Flex>
                <Flex gap={2} justifyContent={'center'} alignItems={'center'}>
                    <Box cursor={'pointer'} onClick={() => handleShowHideFormOpen()} mr={2}>
                        {isStaticReportType ? <FiPlusCircle size={22} /> : <LucideEdit size={22} />}
                    </Box>
                    {/* <Button size="sm" variant="outline" onClick={expandAll} leftIcon={<MdExpand />}>
                        Expand All
                    </Button>
                    <Button size="sm" variant="outline" onClick={collapseAll} leftIcon={<MdCompress />}>
                        Collapse All
                    </Button> */}
                </Flex>
            </Flex>

            <Box mt="15px" bg={backgroundColor} px={4} py={0} borderRadius="7px">
                <Flex alignItems="center" gap={3} justifyContent="end">
                    <Box my={2} width="300px" fontSize="13px" fontWeight={500} display={{ base: 'none', xl: 'block' }}>
                        <GridItem className="seperator-width" mt={5}>
                            {renderDynamicDropdown(
                                'company',
                                'Company',
                                lastcompanyOptions,
                                { value: companyFilter, onChange: handleCompanyChange },
                                {},
                                textColor,
                                false,
                                'Select company',
                                true,
                                undefined,
                                true,
                            )}
                        </GridItem>
                    </Box>

                    <Box flex="1" display={{ base: 'none', xl: 'flex' }}>
                        <Flex alignItems="center" mt={3}>
                            <PeriodDatePicker
                                value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                                onChange={handleDateRangeChange}
                                defaultPeriod="last7Days"
                                style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                            />
                        </Flex>

                        <Box my={2} zIndex={3} width={'150px'} fontSize={'13px'} fontWeight={500} mr={2}>
                            <label>Report Type</label>
                            <Select
                                options={reportTypeOptions}
                                formatOptionLabel={formatOptionLabel}
                                value={reportTypeOptions.find((option) => option.value === reportType)}
                                onChange={handleReportTypeChange}
                                placeholder="Select Report Type"
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        minHeight: '30px',
                                        height: '30px',
                                        backgroundColor: controlBg,
                                        borderColor: controlBorder,
                                        boxShadow: state.isFocused ? focusBoxShadow : baseStyles.boxShadow,
                                        '&:hover': {
                                            borderColor: controlBorderHover,
                                        },
                                    }),
                                    valueContainer: (baseStyles) => ({
                                        ...baseStyles,
                                        height: '30px',
                                        padding: '0 6px',
                                        color: textColor,
                                    }),
                                    indicatorsContainer: (baseStyles) => ({
                                        ...baseStyles,
                                        height: '30px',
                                    }),
                                    singleValue: (baseStyles) => ({
                                        ...baseStyles,
                                        color: textColor,
                                    }),
                                    placeholder: (baseStyles) => ({
                                        ...baseStyles,
                                        color: placeholderColor,
                                    }),
                                    option: (baseStyles, state) => ({
                                        ...baseStyles,
                                        backgroundColor: state.isFocused ? optionHoverBg : optionBg,
                                        color: textColor,
                                        fontWeight: state.data?.isDynamic ? 'bold' : 'normal', // Style dynamic options differently
                                        '&:hover': {
                                            backgroundColor: optionHoverBg,
                                        },
                                    }),
                                    groupHeading: (baseStyles) => ({
                                        ...baseStyles,
                                        backgroundColor: menuBg,
                                        border: `1px solid ${menuBorder}`,
                                    }),
                                    dropdownIndicator: (baseStyles) => ({
                                        ...baseStyles,
                                        color: indicatorColor,
                                        '&:hover': {
                                            color: indicatorHoverColor,
                                        },
                                    }),
                                    clearIndicator: (baseStyles) => ({
                                        ...baseStyles,
                                        color: indicatorColor,
                                        '&:hover': {
                                            color: indicatorHoverColor,
                                        },
                                    }),
                                }}
                            />
                        </Box>
                        <Box my={2} zIndex={3} width={'150px'} fontSize={'13px'} fontWeight={500} mr={2}>
                            <label>Stock Status</label>
                            <Select
                                options={stockReportTypeOptions}
                                formatOptionLabel={formatOptionLabel}
                                value={stockReportTypeOptions.find((option) => option.value === stockStatus)}
                                onChange={handleStockStatusChange}
                                placeholder="Select Stock Status"
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        minHeight: '30px',
                                        height: '30px',
                                        backgroundColor: controlBg,
                                        borderColor: controlBorder,
                                        boxShadow: state.isFocused ? focusBoxShadow : baseStyles.boxShadow,
                                        '&:hover': {
                                            borderColor: controlBorderHover,
                                        },
                                    }),
                                    valueContainer: (baseStyles) => ({
                                        ...baseStyles,
                                        height: '30px',
                                        padding: '0 6px',
                                        color: textColor,
                                    }),
                                    indicatorsContainer: (baseStyles) => ({
                                        ...baseStyles,
                                        height: '30px',
                                    }),
                                    singleValue: (baseStyles) => ({
                                        ...baseStyles,
                                        color: textColor,
                                    }),
                                    placeholder: (baseStyles) => ({
                                        ...baseStyles,
                                        color: placeholderColor,
                                    }),
                                    option: (baseStyles, state) => ({
                                        ...baseStyles,
                                        backgroundColor: state.isFocused ? optionHoverBg : optionBg,
                                        color: textColor,
                                        fontWeight: state.data?.isDynamic ? 'bold' : 'normal', // Style dynamic options differently
                                        '&:hover': {
                                            backgroundColor: optionHoverBg,
                                        },
                                    }),
                                    groupHeading: (baseStyles) => ({
                                        ...baseStyles,
                                        backgroundColor: menuBg,
                                        border: `1px solid ${menuBorder}`,
                                    }),
                                    dropdownIndicator: (baseStyles) => ({
                                        ...baseStyles,
                                        color: indicatorColor,
                                        '&:hover': {
                                            color: indicatorHoverColor,
                                        },
                                    }),
                                    clearIndicator: (baseStyles) => ({
                                        ...baseStyles,
                                        color: indicatorColor,
                                        '&:hover': {
                                            color: indicatorHoverColor,
                                        },
                                    }),
                                }}
                            />
                        </Box>

                        {/* <Box my={2} zIndex={3} fontSize="13px" fontWeight={500}>
                     <label>Format</label>
                     <Select
                        options={FORMAT_TYPE_OPTIONS}
                        value={FORMAT_TYPE_OPTIONS.find((option) => option.value === formatType)}
                        onChange={(selectedOption) => {
                           setFormatType(selectedOption.value);
                           setExpandedItems({});
                        }}
                        placeholder="Select Format Type"
                        styles={{
                           control: (baseStyles, state) => ({
                              ...baseStyles,
                              minHeight: '30px',
                              height: '30px',
                              backgroundColor: controlBg,
                              borderColor: controlBorder,
                              boxShadow: state.isFocused ? focusBoxShadow : baseStyles.boxShadow,
                              '&:hover': {
                                 borderColor: controlBorderHover,
                              },
                           }),
                           valueContainer: (baseStyles) => ({
                              ...baseStyles,
                              height: '30px',
                              padding: '0 6px',
                              color: textColor,
                           }),
                           indicatorsContainer: (baseStyles) => ({
                              ...baseStyles,
                              height: '30px',
                           }),
                           singleValue: (baseStyles) => ({
                              ...baseStyles,
                              color: textColor,
                           }),
                           placeholder: (baseStyles) => ({
                              ...baseStyles,
                              color: placeholderColor,
                           }),
                           option: (baseStyles, state) => ({
                              ...baseStyles,
                              backgroundColor: state.isFocused ? optionHoverBg : optionBg,
                              color: textColor,
                              '&:hover': {
                                 backgroundColor: optionHoverBg,
                              },
                           }),
                           menu: (baseStyles) => ({
                              ...baseStyles,
                              backgroundColor: menuBg,
                              border: `1px solid ${menuBorder}`,
                           }),
                           dropdownIndicator: (baseStyles) => ({
                              ...baseStyles,
                              color: indicatorColor,
                              '&:hover': {
                                 color: indicatorHoverColor,
                              },
                           }),
                           clearIndicator: (baseStyles) => ({
                              ...baseStyles,
                              color: indicatorColor,
                              '&:hover': {
                                 color: indicatorHoverColor,
                              },
                           }),
                        }}
                     />
                  </Box> */}
                    </Box>

                    <Flex mr={2}>
                        <Box flex="1" mr={3}>
                            <MotionFlex
                                ref={wrapperRef}
                                align="center"
                                bg={bg}
                                borderRadius="full"
                                cursor="pointer"
                                px={expanded ? 3 : 0}
                                w={expanded ? '250px' : '40px'}
                                h="40px"
                                transition="all 0.3s ease"
                                onClick={() => setExpanded(true)}>
                                {isLoading ? (
                                    <Spinner size="sm" color={icon} />
                                ) : (
                                    <MotionSearchIcon
                                        color={icon}
                                        animate={{ rotate: expanded ? 0 : 90 }}
                                        transition={{ duration: 0.3 }}
                                        style={{
                                            marginRight: expanded ? '8px' : '10px',
                                            marginLeft: expanded ? '0' : '10px',
                                        }}
                                    />
                                )}
                                {expanded && (
                                    <InputGroup width="200px">
                                        <Input
                                            variant="unstyled"
                                            placeholder="Search ..."
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            autoFocus
                                            color={icon}
                                            flex="1"
                                        />
                                    </InputGroup>
                                )}
                            </MotionFlex>
                        </Box>

                        <Button onClick={() => fetchData()} _hover={{ bg: 'blue.50' }} mr={2}>
                            <MdOutlineRefresh size={20} mr={2} />
                        </Button>

                        <Button cursor={'pointer'} onClick={onFilterOpen} mr={2}>
                            <IoFilterOutline size={22} />
                        </Button>

                        <Tooltip label="Show/Hide Columns">
                            <IconButton
                                icon={<ViewIcon />}
                                onClick={() => setIsShowHideColumn(true)}
                                aria-label="Column Visibility"
                            />
                        </Tooltip>
                    </Flex>

                    <Flex mr={2}>
                        <Popover>
                            <PopoverTrigger>
                                <IconButton icon={<Image src={Download} boxSize="24px" />} aria-label="Download Options" />
                            </PopoverTrigger>
                            <PopoverContent width="220px" zIndex="10" bg={backgroundColor} mr={8}>
                                <PopoverBody p="">
                                    <Button
                                        width="100%"
                                        variant="ghost"
                                        _hover={{ bg: 'blue.100' }}
                                        onClick={downloadExcel}
                                        justifyContent="space-between"
                                        mb={3}>
                                        <img src={xls} alt="Download XLS" width="30px" />
                                        Download Excel
                                    </Button>
                                    <Button
                                        width="100%"
                                        variant="ghost"
                                        _hover={{ bg: 'blue.100' }}
                                        onClick={downloadPDF}
                                        justifyContent="space-between">
                                        <img src={xls} alt="Download pdf" width="30px" mr />
                                        Download PDF
                                    </Button>
                                </PopoverBody>
                            </PopoverContent>
                        </Popover>
                    </Flex>
                    <Box ml={1}>
                        <Flex display={{ base: 'block', xl: 'none' }}>
                            <Button variant="unstyled" onClick={onOpen}>
                                <Image src={DotIcon} alt="Menu" width="25px" />
                            </Button>
                        </Flex>
                    </Box>

                    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} finalFocusRef={fromDateInputRef}>
                        <DrawerOverlay />
                        <DrawerContent borderTopRadius="20px">
                            <DrawerCloseButton />
                            <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
                            <DrawerBody>
                                <GridItem className="seperator-width">
                                    {renderDynamicDropdown(
                                        'company',
                                        'Company',
                                        lastcompanyOptions,
                                        { value: companyFilter, onChange: handleCompanyChange },
                                        {},
                                        textColor,
                                        false,
                                        'Select company',
                                        true,
                                        undefined,
                                        true,
                                    )}
                                </GridItem>
                                <Box my={2} fontSize="13px" fontWeight={500}>
                                    <label>Report Type</label>
                                    <Select
                                        options={REPORT_TYPE_OPTIONS}
                                        value={REPORT_TYPE_OPTIONS.find((option) => option.value === reportType)}
                                        onChange={(selectedOption) => {
                                            setReportType(selectedOption.value);
                                            setExpandedItems({});
                                        }}
                                        placeholder="Select Report Type"
                                    />
                                </Box>
                                <Box alignItems="center" mt={3}>
                                    <PeriodDatePicker
                                        value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                                        onChange={handleDateRangeChange}
                                        defaultPeriod="last7Days"
                                        style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                                    />
                                </Box>

                                <Button colorScheme="green" width="100%" onClick={onClose} mt={4}>
                                    Apply Filters
                                </Button>
                            </DrawerBody>
                        </DrawerContent>
                    </Drawer>
                </Flex>
            </Box>

            <Grid overflow="auto">
                <Card mt="15px" borderRadius="7px">
                    <Box
                        overflowX="auto"
                        className="custom-scrollbar"
                        position="relative"
                        width="100%"
                        height={`${containerHeight}px`}
                        id="sales-detail-container">
                        {isFilterLoading && (
                            <Box
                                position="fixed"
                                top={0} left={0} right={0} bottom={0}
                                zIndex={9999}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bg={isDark ? 'rgba(10,20,50,0.60)' : 'rgba(255,255,255,0.60)'}
                                backdropFilter="blur(3px)"
                            >
                                <Flex direction="column" align="center" gap={3}>
                                    <Loader />
                                </Flex>
                            </Box>
                        )}
                        {isLoading ? (
                            <Flex justifyContent="center" alignItems="center" height="100%">
                                <Loader />
                            </Flex>
                        ) : flattenedData.length === 0 ? (
                            <Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
                                <Flex direction="column" align="center" justify="center">
                                    <img src={nodata} alt="No Data Found" width="200px" height="200px" />
                                    <Text mt={4}>No sales data found</Text>
                                </Flex>
                            </Box>
                        ) : (
                            <VariableSizeList
                                ref={listRef}
                                height={containerHeight}
                                width="100%"
                                itemCount={flattenedData.length}
                                itemSize={getItemSize}
                                overscanCount={15}
                                estimatedItemSize={50}>
                                {RowRenderer}
                            </VariableSizeList>
                        )}
                    </Box>
                </Card>
            </Grid>

            <Columnshowhide
                isOpen={isShowHideColumn}
                onClose={() => setIsShowHideColumn(false)}
                data={columnsData}
                setShowHideData={setShowHideData}
            />

            <ColumnVisibilityModal
                isOpen={isColumnSelectorOpen}
                onClose={() => setIsColumnSelectorOpen(false)}
                columns={dynamicColumns}
                columnVisibility={columnVisibility}
                setColumnVisibility={table.setColumnVisibility}
            />

            <SalesPurchaseFilter
                type={reportType}
                isOpen={isFilterOpen}
                onClose={onFilterClose}
                companyIds={companyIds}
                fromDate={dateRange.fromDate}
                toDate={dateRange.toDate}
                entityType="salesOrderDetail"
                onApply={() => setIsFilterLoading(true)}
            />

            <ColumnShowHide
                isOpen={isShowHideColumn}
                onClose={() => setIsShowHideColumn(false)}
                data={columnsData}
                setShowHideData={setShowHideData}
                isedit={isEditShowHide}
                editData={editData}
                selectedFormat={reportTypeOptions.dynamicOptions}
                FixdefName1={'SalesOrderDetailDate'}
                FixdefName2={'SalesOrderDetailParty'}
                FixdefName3={'SalesOrderDetailBroker'}
                staticOption={staticOption}
                dynamicEditOption={dynamicEditOption}
            />

            <DeletePasswordModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={deleteRecordForSales}
                id={dataId}
                deleteApi={deleteSaleOrder}
                dispatch={dispatch}
                type="purchase"
                userGlobalSettingsData={userGlobalSettingsData}
            />

            {isOpenPurchaseReturnSingleView && (
                <div className="fixed inset-0 z-[999]">
                    {isModalLoading ? (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                            <Loader />
                        </div>
                    ) : viewDetail ? (
                        <SalesDetails
                            isOpen={isOpenPurchaseReturnSingleView}
                            onClose={handleSalesDetailsClose}
                            singleEntityRights={saleOrderRight}
                            title={'Sales Order'}
                            entityType={'saleorder'}
                            singleEntity={viewDetail}
                            handlePrevPage={handlePrevPage}
                            handleNextPage={handleNextPage}
                            setIsEditFormOpen={setIsEditFormOpen}
                            setIsOpenSingleView={setIsOpenPurchaseReturnSingleView}
                            isEditFormOpen={isEditFormOpen}
                            // paginate={paginateData}
                            viewDetail={viewDetail}
                            lineItems={viewDetail?.lineitems}
                            billTerms={viewDetail?.billTerms}
                            onDelete={deletePurchaseReturn}
                        />
                    ) : (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                            <Loader />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default SalesOrderDetail;
